import {
  socialPostDraftSchema,
  socialPostEditSchema,
  hasSocialPostContent,
  socialPostSourceSchema,
  socialCursorSchema,
  SocialPost,
  SocialPostPage,
  SocialPostDetail,
  SocialLikerPage,
} from '../social-post'
import { PerpOrder } from '../perps/orders'
import { PerpSuggestion } from '../perps/suggestion'
import { MNX_LINK_LOCATIONS } from 'common/perps/mnx-cta'
import { MnxDashboard, perpConfigFields } from 'common/perps/management'
import { randomStringRegex } from 'common/util/random'
import type { BrowsePersonalization } from 'common/browse-personalization'
import { MAX_ANSWER_LENGTH, type Answer } from 'common/answer'
import { coerceBoolean, contentSchema } from 'common/api/zod-types'
import { AnyBalanceChangeType } from 'common/balance-change'
import type { Bet, LimitBet } from 'common/bet'
import { ChatMessage, PrivateChatMessage } from 'common/chat-message'
import {
  Comment,
  CommentWithTotalReplies,
  MAX_COMMENT_LENGTH,
  PostComment,
  type ContractComment,
} from 'common/comment'
import {
  AIGeneratedMarket,
  Contract,
  CREATEABLE_OUTCOME_TYPES,
  MarketContract,
} from 'common/contract'
import { Dashboard } from 'common/dashboard'
import { SWEEPS_MIN_BET } from 'common/economy'
import {
  Group,
  LiteGroup,
  MAX_ID_LENGTH,
  MySearchGroupShape,
  SearchGroupParams,
  SearchGroupShape,
  Topic,
} from 'common/group'
import { MARKET_LINK_RELATIONS, MarketLinks } from 'common/market-links'
import {
  JOB_INTERESTS,
  JOB_REGIONS,
  JOB_SKILLS,
  JobSeekerInterest,
} from 'common/job-seeker'
import { League } from 'common/leagues'
import { type LinkPreview } from 'common/link-preview'
import { LiquidityProvision } from 'common/liquidity-provision'
import { CandidateBet } from 'common/new-bet'
import { Headline } from 'common/news'
import { PERIODS } from 'common/period'
import type { PerpTradeActivity } from 'common/perps/activity'
import { PerpCreatorAccount } from 'common/perps/creator-accounts'
import { PerpQuote, perpQuoteSchema } from 'common/perps/quote'
import {
  LivePortfolioMetrics,
  PortfolioMetrics,
} from 'common/portfolio-metrics'
import { Repost } from 'common/repost'
import { ManaSupply } from 'common/stats'
import { SportsMarket } from 'common/sports'
import {
  SPORT_KEY_RE,
  SportInfo,
  SportsScheduleResponse,
  SportsSubtopic,
} from 'common/sports-schedule'
import { SportsLeaderboards } from 'common/sports-leaderboards'
import { CompetitionSwitchState } from 'common/sports-calendar'
import { ScorePollingPanel } from 'common/sports-score-polling'
import { Row } from 'common/supabase/utils'
import type { ManaPayTxn, Txn } from 'common/txn'
import { z } from 'zod'
import { ModReport } from '../mod-report'
import { PrivateUser, User, UserBan } from '../user'
import { type FullMarketSearchResult, searchProps } from './market-search-types'
import {
  FullMarket,
  closePerpPositionSchema,
  createMarketProps,
  createPerpSchema,
  placePerpTradeSchema,
  resolveMarketProps,
  updateMarketProps,
  type LiteMarket,
} from './market-types'
import { DisplayUser, FullUser } from './user-types'

import { ContractMetric } from 'common/contract-metric'
import { PersonalizedManaOfferSummary } from 'common/personalized-mana-offer'
import {
  CheckoutSession,
  GIDXDocument,
  GPSProps,
  PaymentDetail,
  PendingCashoutStatusData,
  cashoutParams,
  cashoutRequestParams,
  checkoutParams,
  verificationParams,
} from 'common/gidx/gidx'
import { Notification } from 'common/notification'
import { RegistrationReturnType } from 'common/reason-codes'
import { NON_POINTS_BETS_LIMIT } from 'common/supabase/bets'
import { PrivateMessageChannel } from 'common/supabase/private-messages'
import { notification_preference } from 'common/user-notification-preferences'

import { JSONContent } from '@tiptap/core'
import { RanksType } from 'common/achievements'
import { MarketDraft } from 'common/drafts'
import { Reaction } from 'common/reaction'
import { ShopItem } from 'common/shop/items'
import { UserEntitlement, ShopOrder } from 'common/shop/types'
import { ChartAnnotation } from 'common/supabase/chart-annotations'
import { Task, TaskCategory } from 'common/todo'
import { TopLevelPost } from 'common/top-level-post'
import { YEAR_MS } from 'common/util/time'
import {
  MAX_WATCHED_MARKETS,
  WATCHED_MARKETS_PAGE_SIZE,
} from 'common/watched-markets'
import { Dictionary } from 'lodash'
// mqp: very unscientific, just balancing our willingness to accept load
// with user willingness to put up with stale data
export const DEFAULT_CACHE_STRATEGY =
  'public, max-age=5, stale-while-revalidate=10'
// Light cache to prevent accidental rapid-fire requests from hitting the server fresh every time
export const LIGHT_CACHE_STRATEGY =
  'public, max-age=1, stale-while-revalidate=0'
const MAX_EXPIRES_AT = 1_000 * YEAR_MS

type APIGenericSchema = {
  // GET is for retrieval, POST is to mutate something, PUT is idempotent mutation (can be repeated safely)
  method: 'GET' | 'POST' | 'PUT'
  //private APIs can only be called from manifold. undocumented endpoints can change or be deleted at any time!
  visibility: 'public' | 'undocumented' | 'private'
  // whether the endpoint requires authentication
  authed: boolean
  // zod schema for the request body (or for params for GET requests)
  props: z.ZodType
  // note this has to be JSON serializable
  returns?: Record<string, any>
  // Cache-Control header. like, 'max-age=60'
  cache?: string
  // whether the endpoint should prefer authentication even if not required
  preferAuth?: boolean
}

let _apiTypeCheck: { [x: string]: APIGenericSchema }
export const API = (_apiTypeCheck = {
  'refresh-all-clients': {
    method: 'POST',
    visibility: 'public',
    props: z.object({ message: z.string().optional() }),
    authed: true,
  },
  'toggle-system-trading-status': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        token: z.enum(['MANA', 'CASH', 'LOAN']),
      })
      .strict(),
    returns: {} as { status: boolean },
  },
  'recover-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
        email: z.string().optional(), // Optional manual email override
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'get-user-info': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as {
      supabaseEmail?: string
      oldEmail?: string
      firebaseEmail?: string
      initialDeviceToken?: string
      initialIpAddress?: string
      // Admin-only audit note for flagged users — served only through this
      // admin-gated endpoint, never the public User surface.
      verificationFlagReason?: string
    },
  },
  'admin-delete-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'admin-set-bonus-eligibility': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
        bonusEligibility: z
          .enum(['verified', 'grandfathered', 'eligible', 'ineligible'])
          .nullable(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'admin-set-prize-eligibility': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
        prizeEligibility: z.enum(['eligible', 'ineligible']).nullable(),
        // When true AND prizeEligibility is 'ineligible', void the user's
        // outstanding entries in unresolved drawings and refund the mana
        // they paid. Use for under-18 cases where the buyer is owed their
        // money back, or confirmed fraud where the pool should be cleaned.
        voidOutstandingEntries: z.boolean().optional(),
        // Optional free-text reason stamped onto each voided entry row
        // (sweepstakes_tickets.voided_reason) for audit. Surfaced in admin
        // UI only.
        reason: z.string().max(500).optional(),
      })
      .strict(),
    returns: {} as {
      success: boolean
      voidedEntryCount: number
      refundedManaTotal: number
    },
  },
  'admin-flag-for-verification': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
        // true = flag (bonusEligibility = 'requires_verification' + reason).
        // false = clear (restore the pre-flag bonus state, else revert to
        // undefined; clears the reason either way).
        flag: z.boolean(),
        // Optional free-text note shown to other admins in the user-info
        // page. Examples: "suspected alt of @other-user", "fraud signal from
        // signup_blocklist", "manual review requested after dispute".
        reason: z.string().max(500).optional(),
      })
      .strict(),
    // bonusEligibility = the resulting state ('requires_verification' on flag;
    // the restored prior value or undefined on clear) so the UI can update
    // optimistically without guessing.
    returns: {} as {
      success: boolean
      bonusEligibility?:
        | 'verified'
        | 'grandfathered'
        | 'eligible'
        | 'ineligible'
        | 'requires_verification'
    },
  },
  'admin-search-users-by-email': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        email: z.string(),
        limit: z.coerce.number().gte(1).lte(100).default(10),
      })
      .strict(),
    returns: [] as Array<{
      user: FullUser
      matchedEmail: string
      matchedOnOldEmail: boolean
    }>,
  },
  'anonymize-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean; newUsername: string; newName: string },
  },
  'admin-get-related-users': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as {
      userId: string
      targetCreatedTime?: number
      matches: Array<{
        visibleUser: FullUser
        matchReasons: (
          | 'ip'
          | 'deviceToken'
          | 'referrer'
          | 'referee'
          | 'managram'
        )[]
        netManagramAmount?: number
        bans: UserBan[]
      }>
    },
  },
  comment: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as ContractComment,
    props: z
      .object({
        contractId: z.string(),
        content: contentSchema.optional(),
        html: z.string().optional(),
        markdown: z.string().optional(),
        replyToCommentId: z.string().optional(),
        replyToAnswerId: z.string().optional(),
        replyToBetId: z.string().optional(),
      })
      .strict(),
  },

  'follow-contract': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: true },
    props: z
      .object({
        contractId: z.string(),
        follow: z.boolean(),
      })
      .strict(),
  },
  'get-contract': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as Contract,
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
  },
  'answer/:answerId': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as Answer,
    props: z.object({ answerId: z.string() }).strict(),
  },
  'market/:contractId/answers': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as Answer[],
    props: z.object({ contractId: z.string() }).strict(),
  },
  'hide-comment': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        commentPath: z.string(),
        action: z.enum(['hide', 'delete']).optional().default('hide'),
      })
      .strict(),
  },
  'edit-comment': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        contractId: z.string(),
        commentId: z.string(),
        content: contentSchema.optional(),
        html: z.string().optional(),
        markdown: z.string().optional(),
      })
      .strict(),
  },
  'leave-review': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        marketId: z.string(),
        review: contentSchema.optional(),
        rating: z.number().gte(0).lte(5).int(),
      })
      .strict(),
  },
  'pin-comment': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ commentPath: z.string() }).strict(),
  },
  comments: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as ContractComment[],
    props: z
      .object({
        contractId: z.string().optional(),
        contractSlug: z.string().optional(),
        afterTime: z.coerce.number().optional(),
        limit: z.coerce.number().gte(0).lte(1000).default(1000),
        page: z.coerce.number().gte(0).default(0),
        userId: z.string().optional(),
        order: z.enum(['likes', 'newest', 'oldest']).optional(),
        isPolitics: coerceBoolean.optional(),
      })
      .strict(),
  },
  'user-comments': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as Comment[],
    props: z
      .object({
        afterTime: z.coerce.number().optional(),
        limit: z.coerce.number().gte(0).lte(1000).default(1000),
        page: z.coerce.number().gte(0).default(0),
        userId: z.string(),
        term: z.string().optional(),
      })
      .strict(),
  },
  bet: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as CandidateBet & { betId: string },
    props: z
      .object({
        contractId: z.string(),
        amount: z.number().gte(SWEEPS_MIN_BET),
        replyToCommentId: z.string().optional(),
        limitProb: z.number().gte(0.01).lte(0.99).optional(),
        expiresMillisAfter: z.number().lt(MAX_EXPIRES_AT).optional(),
        silent: z.boolean().optional(),
        expiresAt: z.number().lt(MAX_EXPIRES_AT).optional(),
        // Used for binary and new multiple choice contracts (cpmm-multi-1).
        outcome: z.enum(['YES', 'NO']).default('YES'),
        //Multi
        answerId: z.string().optional(),
        dryRun: z.boolean().optional(),
        deps: z.array(z.string()).optional(),
        deterministic: z.boolean().optional(),
      })
      .strict(),
  },
  createuser: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { user: User; privateUser: PrivateUser },
    props: z
      .object({
        deviceToken: z.string().optional(),
        adminToken: z.string().optional(),
        visitedContractIds: z.array(z.string()).optional(),
      })
      .strict(),
  },
  'multi-bet': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: [] as (CandidateBet & { betId: string })[],
    props: z
      .object({
        contractId: z.string(),
        amount: z.number().gte(1),
        limitProb: z.number().gte(0).lte(1).optional(),
        expiresAt: z.number().lt(MAX_EXPIRES_AT).optional(),
        answerIds: z.array(z.string()).min(1),
        deterministic: z.boolean().optional(),
      })
      .strict(),
  },
  'multi-sell': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: [] as (CandidateBet & { betId: string })[],
    props: z
      .object({
        contractId: z.string(),
        answerIds: z.array(z.string()).min(1),
        deterministic: z.boolean().optional(),
      })
      .strict(),
  },
  leaderboard: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as { userId: string; score: number }[],
    props: z
      .object({
        groupId: z.string().optional(),
        limit: z.coerce.number().min(1).max(100).default(50),
        token: z.enum(['MANA', 'CASH']).default('MANA'),
        kind: z.enum(['creator', 'profit', 'loss', 'volume', 'referral']),
      })
      .strict(),
  },
  'verify-phone-number': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { status: string },
    props: z
      .object({
        phoneNumber: z.string(),
        code: z.string(),
      })
      .strict(),
  },
  'request-otp': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { status: string },
    props: z
      .object({
        phoneNumber: z.string(),
      })
      .strict(),
  },
  'bet/cancel/:betId': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ betId: z.string() }).strict(),
    returns: {} as LimitBet,
  },
  // sell shares
  'market/:contractId/sell': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as CandidateBet & { betId: string },
    props: z
      .object({
        contractId: z.string(),
        shares: z.number().positive().optional(), // leave it out to sell all shares
        outcome: z.enum(['YES', 'NO']),
        answerId: z.string().optional(), // Required for multi binary markets
        deterministic: z.boolean().optional(),
        deps: z.array(z.string()).optional(),
        sellForUserId: z.string().optional(), // Admin-only: sell for another user
      })
      .strict(),
  },
  // Apply share identities to collapse mixed YES/NO positions on a sum-to-one
  // multi-choice market into all-YES (with at least one zero) and redeem the
  // minimum across outcomes as cash. Pure accounting — no AMM, no fees.
  'market/:contractId/rebalance': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      cashRedeemed: number
      minShares: number
      betCount: number
      loanPaid: number
    },
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
  },
  'get-user-limit-orders-with-contracts': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      bets: LimitBet[]
      contracts: MarketContract[]
    },
    props: z
      .object({
        userId: z.string(),
        count: z.coerce.number().lte(5000),
        includeExpired: coerceBoolean.optional().default(false),
        includeCancelled: coerceBoolean.optional().default(false),
        includeFilled: coerceBoolean.optional().default(false),
      })
      .strict(),
  },
  'get-interesting-groups-from-views': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as (Group & { hasBet: boolean })[],
    props: z
      .object({
        userId: z.string(),
        contractIds: z.array(z.string()).optional(),
      })
      .strict(),
  },
  bets: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as Bet[],
    props: z
      .object({
        id: z.string().optional(),
        userId: z.string().optional(),
        username: z.string().optional(),
        contractId: z.string().or(z.array(z.string())).optional(),
        contractSlug: z.string().optional(),
        answerId: z.string().optional(),
        // market: z.string().optional(), // deprecated, synonym for `contractSlug`
        limit: z.coerce
          .number()
          .gte(0)
          .lte(50000)
          .default(NON_POINTS_BETS_LIMIT),
        before: z.string().optional(),
        after: z.string().optional(),
        beforeTime: z.coerce.number().optional(),
        afterTime: z.coerce.number().optional(),
        order: z.enum(['asc', 'desc']).optional(),
        kinds: z.enum(['open-limit']).optional(),
        minAmount: z.coerce.number().positive().optional(),
        // undocumented fields. idk what a good api interface would be
        filterRedemptions: coerceBoolean.optional(),
        includeZeroShareRedemptions: coerceBoolean.optional(),
        excludeApi: coerceBoolean.optional(),
        commentRepliesOnly: coerceBoolean.optional(),
        count: coerceBoolean.optional(),
        points: coerceBoolean.optional(),
      })
      .strict(),
  },
  'bet-points': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: 'public, max-age=86400, stale-while-revalidate=3600',
    returns: [] as Bet[],
    props: z
      .object({
        contractId: z.string(),
        answerId: z.string().optional(),
        limit: z.coerce.number().gte(0).lte(50000).default(5000),
        beforeTime: z.coerce.number(),
        afterTime: z.coerce.number(),
        filterRedemptions: coerceBoolean.optional(),
        includeZeroShareRedemptions: coerceBoolean.optional(),
      })
      .strict(),
  },
  'unique-bet-group-count': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as { count: number },
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
  },
  'get-daily-changed-metrics-and-contracts': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    cache: 'public, max-age=900, stale-while-revalidate=90', // 15 minute cache
    props: z
      .object({
        limit: z.coerce.number(),
        userId: z.string(),
        balance: z.coerce.number(),
      })
      .strict(),
    returns: {} as {
      manaMetrics: ContractMetric[]
      contracts: MarketContract[]
      manaProfit: number
      manaInvestmentValue: number
      balance: number
    },
  },
  // deprecated. use /bets?username= instead
  'user/:username/bets': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as Bet[],
    props: z
      .object({
        username: z.string(),
        limit: z.coerce.number().gte(0).lte(1000).default(1000),
      })
      .strict(),
  },
  'group/:slug': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as Group,
    props: z.object({ slug: z.string() }),
  },
  'group/:slug/groups': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as { above: LiteGroup[]; below: LiteGroup[] },
    props: z.object({ slug: z.string() }).strict(),
  },
  'group/:slug/dashboards': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as {
      id: string
      title: string
      slug: string
      creatorId: string
    }[],
    props: z.object({ slug: z.string() }).strict(),
  },
  'group/by-id/:id/groups': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as { above: LiteGroup[]; below: LiteGroup[] },
    props: z.object({ id: z.string() }).strict(),
  },
  'group/by-id/:id': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as Group,
    props: z.object({ id: z.string() }).strict(),
  },
  // deprecated. use /markets?groupId= instead
  'group/by-id/:id/markets': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as LiteMarket[],
    props: z
      .object({
        id: z.string(),
        limit: z.coerce.number().gte(0).lte(1000).default(500),
      })
      .strict(),
  },
  'group/:slug/delete': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ slug: z.string() }),
  },
  'group/by-id/:id/delete': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ id: z.string() }),
  },
  'group/:slug/block': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ slug: z.string() }),
  },
  'group/:slug/unblock': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ slug: z.string() }).strict(),
  },
  'group/by-id/:topId/group/:bottomId': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        topId: z.string(),
        bottomId: z.string(),
        remove: z.boolean().optional(),
      })
      .strict(),
    returns: {} as { status: 'success' },
  },
  groups: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as Group[],
    props: z
      .object({
        availableToUserId: z.string().optional(),
        beforeTime: z.coerce.number().int().optional(),
        limit: z.coerce.number().gte(0).lte(1000).default(500),
      })
      .strict(),
  },
  'market/:id': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    returns: {} as LiteMarket | FullMarket,
    cache: DEFAULT_CACHE_STRATEGY,
    props: z.object({ id: z.string(), lite: coerceBoolean.optional() }),
  },
  'market/:id/prob': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      prob?: number
      answerProbs?: { [answerId: string]: number }
    },
    props: z.object({ id: z.string() }).strict(),
  },
  'market-probs': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      [contractId: string]: {
        prob?: number
        answerProbs?: { [answerId: string]: number }
      }
    },
    props: z
      .object({
        ids: z.array(z.string()).max(100),
      })
      .strict(),
  },
  'markets-by-ids': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as Contract[],
    props: z
      .object({
        ids: z.array(z.string()).max(100),
      })
      .strict(),
  },
  // deprecated. use /market/:id?lite=true instead
  'market/:id/lite': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    returns: {} as LiteMarket,
    cache: DEFAULT_CACHE_STRATEGY,
    props: z.object({ id: z.string() }),
  },
  'slug/:slug': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    returns: {} as LiteMarket | FullMarket,
    cache: DEFAULT_CACHE_STRATEGY,
    props: z.object({ slug: z.string(), lite: coerceBoolean.optional() }),
  },
  market: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as LiteMarket,
    props: createMarketProps,
  },
  'market/:contractId/update': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: updateMarketProps,
    returns: {} as { success: true },
  },
  'market/:contractId/close': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    // returns: {} as LiteMarket,
    props: z
      .object({
        contractId: z.string(),
        closeTime: z.number().int().nonnegative().optional(),
      })
      .strict(),
  },
  'market/:contractId/resolve': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { message: string },
    props: resolveMarketProps,
  },
  'market/:contractId/add-liquidity': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as LiquidityProvision,
    props: z
      .object({
        contractId: z.string(),
        amount: z.number().gt(0).finite(),
        // Subsidize a SINGLE answer (its own binary CPMM) instead of the whole market. Omit for
        // the whole-market add. The subsidy lands in that answer's subsidyPool and the per-answer
        // drizzle deepens it losslessly (float-p for cpmm-multi-2) — see add-liquidity.ts.
        answerId: z.string().optional(),
      })
      .strict(),
  },
  'market/:contractId/remove-liquidity': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as LiquidityProvision,
    props: z
      .object({
        contractId: z.string(),
        amount: z.number().gt(0).finite(),
      })
      .strict(),
  },
  'market/:contractId/add-bounty': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as Txn,
    props: z
      .object({
        contractId: z.string(),
        amount: z.number().gt(0).finite(),
      })
      .strict(),
  },
  'market/:contractId/award-bounty': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as Txn,
    props: z
      .object({
        contractId: z.string(),
        commentId: z.string(),
        amount: z.number().gt(0).finite(),
      })
      .strict(),
  },
  'market/:contractId/group': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        contractId: z.string(),
        groupId: z.string(),
        remove: z.boolean().default(false),
      })
      .strict(),
    returns: {} as { success: true },
  },
  'market/:contractId/groups': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z.object({ contractId: z.string() }),
    returns: [] as LiteGroup[],
  },
  'market/:contractId/answer': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { newAnswerId: string },
    props: z
      .object({
        contractId: z.string().max(MAX_ANSWER_LENGTH),
        text: z.string().min(1).max(MAX_ANSWER_LENGTH),
      })
      .strict(),
  },
  'market/:contractId/block': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ contractId: z.string() }).strict(),
  },
  'market/:contractId/unblock': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ contractId: z.string() }).strict(),
  },
  unresolve: {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: true },
    props: z
      .object({
        contractId: z.string().max(MAX_ANSWER_LENGTH),
        answerId: z.string().max(MAX_ANSWER_LENGTH).optional(),
      })
      .strict(),
  },
  'create-perp': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as LiteMarket,
    props: createPerpSchema,
  },
  'internal-perp-order-effects': {
    method: 'POST',
    visibility: 'private',
    authed: false,
    returns: {} as { processed: number },
    props: z
      .object({
        apiSecret: z.string().min(1),
        orderIds: z.array(z.string().min(1)).max(25),
      })
      .strict(),
  },
  'get-perp-order': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: {} as { order: PerpOrder | null },
    props: z.object({ contractId: z.string(), orderId: z.string().optional() }),
    cache: 'no-store',
  },
  'place-perp-trade': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { order: PerpOrder },
    props: placePerpTradeSchema,
  },
  'close-perp-position': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { order: PerpOrder },
    props: closePerpPositionSchema,
  },
  // The open-weight index halts on models it cannot classify. These two
  // endpoints are how that gets cleared without a deploy: the queue of models
  // the nightly watcher could not confirm, and the verdict on one.
  'get-model-classifications': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      pending: {
        permaslug: string
        openRouterName: string | null
        huggingFaceId: string | null
        discoveredVia: string | null
        firstSeen: number
        ageMs: number
        /** Null until the model actually enters the ranked window. */
        firstRankedAt: number | null
        rankedAgeMs: number | null
        /** Past the window: the index is already halting on this one. */
        graceExpired: boolean
        /** The research agent's recommendation, if it ran. Never auto-applied. */
        agentRecommendation: string | null
        agentReasoning: string | null
        /**
         * The tool calls the recommendation rests on, in order. A closed
         * verdict cannot be machine-checked, so this is what the operator
         * actually adjudicates against — the summary alone is not evidence.
         */
        agentSearches: {
          tool: string
          input: string | null
          result: string
        }[]
        /**
         * A repo the agent proposed and the live HuggingFace API confirmed.
         * Never applied automatically — verification and a name match cannot
         * establish that the repo is THIS model's — but prefilled so the
         * operator confirms rather than retypes.
         */
        agentProposedWeights: string | null
        /** Weight files the live API reported for that repo, when known. */
        agentWeightFileCount: number | null
      }[]
      recent: {
        permaslug: string
        open: boolean
        weights: string | null
        source: string
        classifiedAt: number | null
        classifiedBy: string | null
      }[]
      seedVersion: string
      graceWindowMs: number
    },
    props: z.object({}).strict(),
  },
  'set-model-classification': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: true; permaslug: string; open: boolean },
    props: z
      .object({
        permaslug: z.string().min(1),
        open: z.boolean(),
        // The HuggingFace repo proving the weights are downloadable.
        weights: z.string().min(1).optional(),
      })
      .strict()
      // Same invariant the seed list's test and the table's check constraint
      // enforce: an `open` verdict cites its evidence or it does not land.
      // Rejecting here means the operator finds out at the form, not from a
      // constraint violation.
      .refine((p) => !p.open || !!p.weights, {
        message: 'An open classification must cite a public weights repo',
      }),
  },
  // Runtime maintenance for the Chinese-lab OpenRouter index. Ordinary
  // publishers are classified once at author scope; anonymous/shared
  // namespaces can instead receive an exact-model verdict.
  'get-openrouter-lab-classifications': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      pending: {
        subjectType: 'author' | 'model'
        subjectSlug: string
        discoveredVia: string | null
        exampleModels: string[]
        exampleNames: string[]
        firstSeen: number
        firstRankedAt: number | null
      }[]
      decided: {
        subjectType: 'author' | 'model'
        subjectSlug: string
        isChinese: boolean
        evidence: string
        sourceUrl: string | null
        exampleModels: string[]
        exampleNames: string[]
        source: 'auto' | 'admin'
        classifiedAt: number
        classifiedBy: string | null
      }[]
      seedVersion: string
    },
    props: z.object({}).strict(),
  },
  'set-openrouter-lab-classification': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      success: true
      subjectType: 'author' | 'model'
      subjectSlug: string
      isChinese: boolean
    },
    props: z
      .object({
        subjectType: z.enum(['author', 'model']),
        subjectSlug: z.string().trim().min(1).max(300),
        isChinese: z.boolean(),
        evidence: z.string().trim().min(1).max(2_000),
        sourceUrl: z
          .string()
          .trim()
          .url()
          .max(2_000)
          .refine((url) => /^https?:\/\//i.test(url), {
            message: 'Evidence URL must use http or https',
          }),
      })
      .strict(),
  },
  // Admin / MNX-owner live risk tuning; undocumented deliberately — internal
  // operator tooling, not part of the public perp API surface.
  'update-perp-config': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      success: true
      maxLeverage: number
      maxFundingRate: number
      fundingSensitivity: number
      takerFeeBps: number
      takerFeeImpact: number
      // Configured API-channel base rate, or null when API trades pay the
      // same base as the web. The engine applies max(takerFeeBps, this).
      takerFeeApiBps: number | null
      // What an API-key open is actually charged after that max() — equal to
      // takerFeeBps whenever the configured API rate sits at or below the
      // base, which makes such a rate a no-op rather than a reduction.
      effectiveTakerFeeApiBps: number
      maxOraclePriceAgeMs: number
    },
    props: perpConfigFields
      .extend({
        contractId: z.string().min(1),
        // Optimistic check of the values the operator reviewed. Trades do not
        // change these, so normal activity does not invalidate the preview.
        expectedConfig: perpConfigFields.optional(),
        expectedManagerId: z.string().min(1).optional(),
      })
      .strict()
      .refine(
        (p) =>
          Object.keys(perpConfigFields.shape).some(
            (key) => p[key as keyof typeof perpConfigFields.shape] !== undefined
          ),
        { message: 'Provide at least one field to update' }
      ),
  },
  'get-mnx-dashboard': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {} as MnxDashboard,
    props: z.object({}).strict(),
  },
  // Defaults to the signed-in manager; the MNX dashboard uses MNX funds.
  // For 'both', amount is added to EACH side.
  // A request key makes retrying a dashboard operation safe after a timeout.
  'add-perp-subsidy': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: true; poolLong: number; poolShort: number },
    props: z
      .object({
        contractId: z.string().min(1),
        side: z.enum(['long', 'short', 'both']),
        fundingAccount: z.enum(['mnx']).optional(),
        expectedManagerId: z.string().min(1).optional(),
        amount: z.number().finite().gt(0).lte(1_000_000),
        idempotencyKey: z
          .string()
          .regex(randomStringRegex)
          .length(10)
          .optional(),
      })
      .strict(),
  },
  'get-oracle-price': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as {
      latest: {
        feedId: string
        price: number
        ts: number
        sourceTs?: number
      } | null
    },
    props: z.object({ feedId: z.string().min(1) }).strict(),
  },
  'get-oracle-price-series': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as { ts: number; price: number }[],
    props: z
      .object({
        feedId: z.string().min(1),
        since: z.coerce.number().int().optional(),
        // Exclusive upper bound (ms). `limit` always returns the NEWEST points
        // in the window, so `before` is what makes the series pageable: pass
        // the first ts of the previous response to walk backwards to the start
        // of the feed. Without it, `since` alone can only shrink a window that
        // is already anchored to now.
        before: z.coerce.number().int().optional(),
        limit: z.coerce.number().int().positive().max(5000).optional(),
        // Server-side downsampling: return the last point of each
        // `bucketSeconds` bucket instead of raw rows. A 15s feed emits ~5k
        // points/day, so week+ windows must bucket or the `limit` cap turns
        // every timeframe into "the last two days".
        bucketSeconds: z.coerce.number().int().positive().max(86400).optional(),
      })
      .strict(),
  },
  'get-known-oracle-feeds': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    // Registry metadata lets the admin page omit runtime-only feeds from its
    // picker. null updatePeriodMs means the feed has price rows but no
    // OracleFeedDef; false marketCreationEnabled means either unregistered or
    // deliberately runtime-only (create-perp rejects both).
    // The admin create page needs it to convert an annual max funding rate
    // into the per-PERIOD fraction the engine stores — assuming hourly on a
    // daily feed would understate the cap 24x.
    returns: [] as {
      id: string
      // Capability probe for scripts that explicitly set the API-channel fee.
      supportsApiTakerFee: boolean
      updatePeriodMs: number | null
      marketCreationEnabled: boolean
      description: string | null
      // Canonical ticker (PERP_FEED_TICKERS); null for a feed nobody has
      // named, where the form may choose one.
      ticker: string | null
      launchLatencyRisk: string | null
      launchRecommendation: {
        question: string
        maxLeverage: number
        annualMaxFundingRate: number
        fundingSensitivity: number
        maxOraclePriceAgeMs: number
        subsidyLong: number
        subsidyShort: number
        requiredTopicNames: string[]
        creatorAuthorized: boolean
      } | null
      // Whether the caller may create on this feed at all: only the official
      // Manifold account can, because it either pays the backing itself or
      // acts for a partner account (see createPerpSchema.creatorAccount).
      callerAuthorized: boolean
      // Every selectable owner, in display order. `allowed` is feed policy
      // (a partner owns only its own feeds); `unavailableReason` is set when
      // the account cannot be resolved in this environment.
      creatorAccounts: {
        account: PerpCreatorAccount
        label: string
        allowed: boolean
        username: string | null
        unavailableReason: string | null
      }[]
    }[],
    props: z.object({}).strict(),
  },
  'internal-write-oracle-price': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: true },
    props: z
      .object({
        feedId: z.string().min(1).max(200),
        ts: z.number().int().positive(),
        // Provider-declared source data timestamp. Required by the writer for
        // feeds whose attribution terms include an as-of time.
        sourceTs: z.number().int().positive().optional(),
        // The engine divides by entry prices and treats <= 0 as invalid;
        // reject junk at the door rather than poisoning the feed.
        price: z.number().finite().positive(),
      })
      .strict(),
  },
  'get-perp-quote': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // Never edge-cached. This is the price a trader sizes and closes against;
    // DEFAULT_CACHE_STRATEGY's max-age=5 + stale-while-revalidate=10 can serve
    // a body 15s old, which at 100x leverage is a materially different market.
    // See get-perp-positions for the same reasoning applied to positions.
    cache: 'no-cache',
    returns: {} as PerpQuote,
    props: z.object({ contractId: z.string().min(1) }).strict(),
  },
  // Scheduler -> API hop that turns an applied oracle tick into a websocket
  // broadcast. Not called by clients. See perps/publish-perp-quote.ts for why
  // the scheduler cannot broadcast directly.
  //
  // Must stay off the read-replica allowlist in url-map-config.yaml: only the
  // writer process runs the websocket server, so routing this to a replica
  // would drop every push without erroring.
  'internal-perp-broadcast': {
    method: 'POST',
    visibility: 'private',
    authed: false,
    returns: {} as { success: boolean },
    props: z
      .object({
        apiSecret: z.string().min(1),
        quote: perpQuoteSchema,
      })
      .strict(),
  },
  // Scheduler -> API writer; must stay off the read-replica allowlist.
  'internal-sports-broadcast': {
    method: 'POST',
    visibility: 'private',
    authed: false,
    returns: {} as { success: boolean },
    props: z
      .object({
        apiSecret: z.string().min(1),
        contractId: z.string().min(1),
        score: z.object({
          sportsHomeScore: z.number().finite().nonnegative().nullable(),
          sportsAwayScore: z.number().finite().nonnegative().nullable(),
          sportsLiveStatus: z.enum([
            'IN_PLAY',
            'PAUSED',
            'FINISHED',
            'AWARDED',
          ]),
          sportsLiveMinute: z.string().nullable(),
          sportsLiveUpdatedTime: z.number().finite(),
          sportsScoreUpdatedTime: z.number().finite().nullable().optional(),
        }),
      })
      .strict(),
  },
  'get-perp-positions': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // NOT the default 5s+swr cache: the API sits behind an edge cache that
    // serves stale-while-revalidate regardless of client no-cache, so a
    // trader's own just-placed position can vanish from refetches for ~15s.
    // Positions are correctness-critical right after a trade.
    cache: 'no-cache',
    returns: [] as {
      contractId: string
      userId: string
      direction: 'long' | 'short'
      size: number
      costBasis: number
      originalCostBasis: number
      takerFeeCostBasis: number
      entryPrice: number
      leverage: number
      liquidationPrice: number
      openedTime: number
      updatedTime: number
      userName: string | null
      username: string | null
      avatarUrl: string | null
    }[],
    props: z
      .object({
        // One of contractId / userId is required: a market's book, a user's
        // book across every perp (the /perps hub polls that in one call), or
        // one user in one market.
        contractId: z.string().min(1).optional(),
        userId: z.string().min(1).optional(),
      })
      .strict()
      .refine((p) => p.contractId || p.userId, {
        message: 'contractId or userId is required',
      }),
  },
  'get-perp-suggestions': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    // Signed-in viewers get their own hasVoted in the same response.
    preferAuth: true,
    cache: 'no-store',
    returns: [] as PerpSuggestion[],
    props: z
      .object({
        limit: z.coerce.number().int().positive().max(100).optional(),
        // Mods and admins only — silently ignored for everyone else. Returns
        // moderated rows alongside the live ones, each tagged with `hidden`.
        includeHidden: coerceBoolean.optional(),
      })
      .strict(),
  },
  'create-perp-suggestion': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as PerpSuggestion,
    props: z
      .object({
        name: z.string().min(1).max(200),
        dataSource: z.string().max(1000).optional(),
      })
      .strict(),
  },
  'vote-perp-suggestion': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { votes: number },
    props: z
      .object({
        suggestionId: z.number().int().positive(),
        remove: z.boolean().optional(),
      })
      .strict(),
  },
  // Moderation: mods and admins drop a suggestion out of the public list.
  // Reversible — the row is flagged, never deleted, so the votes on it
  // survive an unhide.
  'hide-perp-suggestion': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { hidden: boolean },
    props: z
      .object({
        suggestionId: z.number().int().positive(),
        // Omitted means hide; pass false to restore.
        hide: z.boolean().optional(),
      })
      .strict(),
  },
  'get-perp-events': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // See get-perp-positions: edge-cache staleness makes fresh closes /
    // liquidations lag their tombstones. Funding events and oracle series
    // keep the default cache — append-only data where 5-15s is harmless.
    cache: 'no-cache',
    returns: [] as {
      id: number
      contractId: string
      ts: number
      userId: string | null
      direction: 'long' | 'short' | null
      eventType: 'open' | 'add' | 'close' | 'liquidation' | 'adl' | 'funding'
      oraclePrice: number
      sizeDelta: number
      costBasisDelta: number
      originalCostBasisDelta: number
      leverage: number | null
      payout: number | null
      pnl: number | null
      adlFactor: number | null
      /** Closes only: the fraction of the position this event took. Null on
       * closes written before partial closes existed, which were whole. */
      fraction: number | null
      isApi: boolean
      userName: string | null
      username: string | null
      avatarUrl: string | null
    }[],
    props: z
      .object({
        contractId: z.string().min(1).optional(),
        // A merged, newest-first tape across several markets in ONE query —
        // the /perps hub's activity feed. Exactly one of contractId /
        // contractIds.
        contractIds: z.array(z.string().min(1)).min(1).max(50).optional(),
        userId: z.string().min(1).optional(),
        beforeId: z.coerce.number().int().optional(),
        limit: z.coerce.number().int().positive().max(200).optional(),
        // Drops trades placed with an API key. Liquidations and ADL stay:
        // they aren't anyone's order. Events written before the flag shipped
        // carry no marker and read as manual.
        excludeApi: coerceBoolean.optional(),
      })
      .strict()
      .refine((p) => !!p.contractId !== !!p.contractIds, {
        message: 'Exactly one of contractId or contractIds is required',
      }),
  },
  'get-perp-funding-events': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as {
      ts: number
      fundingRate: number
      oraclePrice: number
      numLiquidations: number
      adlFactorLong: number
      adlFactorShort: number
    }[],
    props: z
      .object({
        contractId: z.string().min(1),
        since: z.coerce.number().int().optional(),
        limit: z.coerce.number().int().positive().max(5000).optional(),
      })
      .strict(),
  },
  leagues: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as League[],
    props: z
      .object({
        userId: z.string().optional(),
        cohort: z.string().optional(),
        season: z.coerce.number().optional(),
      })
      .strict(),
  },
  markets: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as LiteMarket[],
    props: z
      .object({
        limit: z.coerce.number().gte(0).lte(1000).default(500),
        sort: z
          .enum([
            'created-time',
            'updated-time',
            'last-bet-time',
            'last-comment-time',
          ])
          .optional(),
        order: z.enum(['asc', 'desc']).optional(),
        before: z.string().optional(),
        userId: z.string().optional(),
        groupId: z.string().optional(),
      })
      .strict(),
  },
  'search-markets': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // Both search handlers accept optional auth and use it for visibility,
    // blocks, and personalized ranking. The edge cache does not vary on the
    // Authorization header, so public caching can serve one user's result to
    // another user (or to an anonymous caller).
    cache: 'private, no-store',
    returns: [] as LiteMarket[],
    props: searchProps,
  },
  'search-markets-full': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    preferAuth: true,
    cache: 'private, no-store',
    returns: [] as FullMarketSearchResult[],
    props: searchProps,
  },
  'recent-markets': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true, // authed and no cache means users won't accidentally see results from other users
    returns: [] as Contract[],
    props: searchProps,
  },
  managram: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        amount: z.number().finite(),
        toIds: z.array(z.string()),
        message: z.string().max(MAX_COMMENT_LENGTH),
        groupId: z.string().max(MAX_ID_LENGTH).optional(),
        postId: z.string().max(MAX_ID_LENGTH).optional(),
        token: z.enum(['M$', 'CASH']).default('M$'),
      })
      .strict(),
  },
  manalink: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { slug: string },
    props: z
      .object({
        amount: z.number().positive().finite().safe(),
        expiresTime: z.number().lt(MAX_EXPIRES_AT).optional(),
        maxUses: z.number().optional(),
        message: z.string().optional(),
      })
      .strict(),
  },
  donate: {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        amount: z.number().positive().finite().safe(),
        to: z.string(),
      })
      .strict(),
  },
  'convert-sp-to-mana': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ amount: z.number().positive().finite().safe() }).strict(),
  },
  'convert-cash-to-mana': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ amount: z.number().positive().finite().safe() }).strict(),
  },
  'request-loan': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      amount: z.number().positive(),
      contractId: z.string().optional(),
      answerId: z.string().optional(),
    }),
    returns: {} as {
      success: boolean
      amount: number
      distributed: Array<{
        contractId: string
        answerId: string | null
        loanAmount: number
      }>
    },
  },
  'repay-loan': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      amount: z.number().positive(),
      contractId: z.string().optional(),
      answerId: z.string().optional(),
    }),
    returns: {} as { repaid: number; remainingLoan: number },
  },
  'get-total-loan-amount': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}),
    returns: {} as { totalOwed: number },
  },
  // deprecated. use /txns instead
  managrams: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as ManaPayTxn[],
    props: z
      .object({
        toId: z.string().optional(),
        fromId: z.string().optional(),
        limit: z.coerce.number().gte(0).lte(100).default(100),
        before: z.coerce.number().optional(),
        after: z.coerce.number().optional(),
      })
      .strict(),
  },
  'get-post-tip-info': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: {} as { amountTippedByUser: number },
    props: z
      .object({
        postId: z.string(),
      })
      .strict(),
  },
  'market/:id/positions': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as ContractMetric[],
    props: z
      .object({
        id: z.string(),
        userId: z.string().optional(),
        answerId: z.string().optional(),
        summaryOnly: z.boolean().optional(),
        top: z.undefined().or(z.coerce.number()),
        bottom: z.undefined().or(z.coerce.number()),
        order: z.enum(['shares', 'profit']).optional(),
      })
      .strict(),
  },
  'comment-threads': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    props: z
      .object({
        contractId: z.string(),
        limit: z.coerce.number().gte(0).lte(100).default(10),
        page: z.coerce.number().gte(0).default(0),
      })
      .strict(),
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as {
      replyComments: ContractComment[]
      parentComments: ContractComment[]
    },
  },
  'comment-thread': {
    method: 'GET',
    cache: LIGHT_CACHE_STRATEGY,
    visibility: 'public',
    authed: false,
    props: z
      .object({
        contractId: z.string(),
        commentId: z.string(),
      })
      .strict(),
    returns: {} as {
      parentComment: ContractComment | null
      replyComments: ContractComment[]
      parentComments: ContractComment[]
      nextParentComments: ContractComment[]
      nextReplyComments: ContractComment[]
    },
  },
  me: {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z.object({}),
    returns: {} as FullUser,
  },
  'me/update': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({
      userId: z.string().optional(), // Admin-only: specify user to update
      name: z.string().trim().min(1).optional(),
      username: z.string().trim().min(1).optional(),
      avatarUrl: z.string().optional(),
      bio: z.string().optional(),
      website: z.string().optional(),
      twitterHandle: z.string().optional(),
      discordHandle: z.string().optional(),
      // settings
      optOutBetWarnings: z.boolean().optional(),
      isAdvancedTrader: z.boolean().optional(),
      hideApiTrades: z.boolean().optional(),
      showOrderBook: z.boolean().optional(),
      //internal
      seenStreakModal: z.boolean().optional(),
      shouldShowWelcome: z.boolean().optional(),
      hasSeenContractFollowModal: z.boolean().optional(),
      hasSeenLoanModal: z.boolean().optional(),
      hasSeenPerpsExplainer: z.boolean().optional(),
      lastShopVisitTime: z.number().optional(),
    }),
    returns: {} as FullUser,
  },
  'me/delete': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({
      username: z.string(), // just so you're sure
    }),
  },
  'me/private': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z.object({}),
    returns: {} as PrivateUser,
  },
  'me/private/update': {
    method: 'POST',
    visibility: 'private',
    authed: true,
    props: z
      .object({
        email: z.string().email().optional(),
        apiKey: z.string().optional(),
        pushToken: z.string().optional(),
        rejectedPushNotificationsOn: z.number().optional(),
        lastPromptedToEnablePushNotifications: z.number().optional(),
        interestedInPushNotifications: z.boolean().optional(),
        hasSeenAppBannerInNotificationsOn: z.number().optional(),
        installedAppPlatforms: z.array(z.string()).optional(),
        paymentInfo: z.string().optional(),
        lastAppReviewTime: z.number().optional(),
        optOutAppReviewPrompts: z.boolean().optional(),
      })
      .strict(),
  },
  'get-user-private-data': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z.object({
      userId: z.string(),
    }),
    returns: {} as PrivateUser,
  },
  'user/:username': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as FullUser,
    props: z.object({ username: z.string() }).strict(),
  },
  'user/:username/lite': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as DisplayUser,
    props: z.object({ username: z.string() }).strict(),
  },
  'user/by-id/:id': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // Do not add a caching strategy here. New users need up-to-date data.
    returns: {} as FullUser,
    props: z.object({ id: z.string() }).strict(),
  },
  'user/by-id/:id/lite': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as DisplayUser,
    props: z.object({ id: z.string() }).strict(),
  },
  'users/by-id': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as DisplayUser[],
    props: z.object({ ids: z.array(z.string()) }).strict(),
  },
  'users/by-id/balance': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as { id: string; balance: number }[],
    props: z.object({ ids: z.array(z.string()) }).strict(),
  },
  'user/by-id/:id/block': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ id: z.string() }).strict(),
  },
  'user/by-id/:id/unblock': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({ id: z.string() }).strict(),
  },
  users: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as FullUser[],
    props: z
      .object({
        limit: z.coerce.number().gte(0).lte(1000).default(500),
        order: z.enum(['asc', 'desc']).optional().default('desc'),
        before: z.string().optional(),
      })
      .strict(),
  },
  'search-users': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as FullUser[],
    props: z
      .object({
        term: z.string(),
        limit: z.coerce.number().gte(0).lte(1000).default(500),
        page: z.coerce.number().gte(0).default(0),
      })
      .strict(),
  },
  'search-contract-positions': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as DisplayUser[],
    props: z
      .object({
        term: z.string(),
        contractId: z.string(),
        limit: z.coerce.number().gte(0).lte(100).default(10),
      })
      .strict(),
  },
  'save-twitch': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        twitchInfo: z.object({
          twitchName: z.string().optional(),
          controlToken: z.string().optional(),
          botEnabled: z.boolean().optional(),
          needsRelinking: z.boolean().optional(),
        }),
      })
      .strict(),
  },
  'set-push-token': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ pushToken: z.string() }).strict(),
  },
  'update-notif-settings': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      type: z.string() as z.ZodType<notification_preference>,
      medium: z.enum(['email', 'browser', 'mobile']),
      enabled: z.boolean(),
    }),
  },
  'set-job-interest': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        skills: z.array(z.enum(JOB_SKILLS)).max(JOB_SKILLS.length),
        interests: z.array(z.enum(JOB_INTERESTS)).max(JOB_INTERESTS.length),
        region: z.enum(JOB_REGIONS).nullable(),
        openToContact: z.boolean(),
      })
      .strict(),
  },
  'get-mnx-invite-link': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    cache: 'private, no-store',
    props: z
      .object({
        feedId: z.string(),
        location: z.enum(MNX_LINK_LOCATIONS),
      })
      .strict(),
    returns: {} as { url: string },
  },
  'get-job-interest': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { interest: JobSeekerInterest | null },
  },
  headlines: {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as Headline[],
    props: z.object({
      slug: z.enum(['politics', 'ai', 'news']).optional(),
    }),
  },
  'politics-headlines': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as Headline[],
    props: z.object({}),
  },
  'set-news': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: true },
    props: z
      .object({
        dashboardIds: z.array(z.string()),
        endpoint: z.enum(['politics', 'ai', 'news']),
      })
      .strict(),
  },
  react: {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        contentId: z.string(),
        contentType: z.enum(['comment', 'contract', 'post', 'social_post']),
        commentParentType: z.enum(['post']).optional(),
        remove: z.boolean().optional(),
        reactionType: z.enum(['like', 'dislike']).optional().default('like'),
      })
      .strict(),
    returns: { success: true },
  },
  post: {
    method: 'POST',
    visibility: 'private',
    authed: true,
    returns: {} as ContractComment,
    props: z
      .object({
        contractId: z.string(),
        betId: z.string().optional(),
        commentId: z.string().optional(),
        content: contentSchema.optional(),
      })
      .strict(),
  },
  'fetch-link-preview': {
    method: 'GET',
    visibility: 'private',
    authed: false,
    cache: 'max-age=86400, stale-while-revalidate=86400',
    props: z.object({ url: z.string() }).strict(),
    returns: {} as LinkPreview,
  },
  'get-related-markets': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=300, stale-while-revalidate=30',
    props: z
      .object({
        contractId: z.string(),
        limit: z.coerce.number().gte(0).lte(100),
        userId: z.string().optional(),
        question: z.string().optional(),
        uniqueBettorCount: z.coerce.number().gte(0).optional(),
      })
      .strict(),
    returns: {} as {
      marketsFromEmbeddings: Contract[]
    },
  },
  'get-related-markets-by-group': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'no-store',
    returns: {} as {
      groupContracts: Contract[]
    },
    props: z
      .object({
        contractId: z.string(),
        limit: z.coerce.number().gte(0).lte(100),
        offset: z.coerce.number().gte(0),
      })
      .strict(),
  },
  'get-market-context': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=3600, stale-while-revalidate=10',
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
    returns: {} as {
      context: JSONContent | undefined
    },
  },
  'set-bot-status': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      userId: z.string(),
      isBot: z.boolean(),
    }),
    returns: {} as { success: boolean },
  },
  'ban-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
        unban: z.boolean().optional(),
        unbanTime: z.number().optional(),
        bans: z
          .object({
            posting: z.boolean().optional(),
            marketControl: z.boolean().optional(),
            trading: z.boolean().optional(),
            purchase: z.boolean().optional(),
            modAlert: z.boolean().optional(), // false to clear active mod alert
          })
          .optional(),
        unbanTimes: z
          .object({
            posting: z.number().optional(),
            marketControl: z.number().optional(),
            trading: z.number().optional(),
            purchase: z.number().optional(),
            modAlert: z.number().optional(), // mod alerts don't auto-expire, but included for type consistency
          })
          .optional(),
        reason: z.string().optional(),
        modAlert: z
          .object({
            message: z.string(),
          })
          .optional(),
        unbanNote: z.string().optional(), // mod notes when removing a ban (not shown to user)
        // Username change restriction - defaults to restricting when any ban is applied
        // Set to true to allow username changes, false to restrict, undefined to use default behavior
        allowUsernameChange: z.boolean().optional(),
        // Remove all active bans at once, creates a single combined history record
        removeAllBans: z.boolean().optional(),
        // Clear a specific mod alert by ID (used when multiple alerts exist)
        clearAlertId: z.number().optional(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'dismiss-mod-alert': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        alertId: z.number().optional(), // Specific alert to dismiss, or all if not provided
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'super-ban-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean; skippedMarketCleanup: boolean },
  },
  'admin-ip-ban-user': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean; added: boolean; ipAddress: string },
  },
  'get-user-bans': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as {
      bans: {
        id: number
        user_id: string
        ban_type: string
        reason: string | null
        created_at: string
        created_by: string | null
        end_time: string | null
        ended_by: string | null
        ended_at: string | null
      }[]
    },
  },
  'get-boost-analytics': {
    method: 'POST',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
    returns: {} as {
      uniqueViewers: number
      totalViews: number
      uniquePromotedViewers: number
      totalPromotedViews: number
      boostPeriods: {
        startTime: string
        endTime: string
        creatorName: string
        creatorUsername: string
      }[]
    },
  },
  'get-boost-history': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    props: z
      .object({
        contractId: z.string().optional(),
        postId: z.string().optional(),
        userId: z.string().optional(),
        // GET query params arrive as strings, so booleans/numbers need coercion.
        includePending: coerceBoolean.optional(),
        limit: z.coerce.number().int().positive().max(1000).default(100),
        offset: z.coerce.number().int().min(0).default(0),
      })
      .strict(),
    returns: {} as {
      boosts: {
        id: number
        contentType: 'contract' | 'post'
        contentId: string
        contractId: string | null
        postId: string | null
        title: string
        slug: string | null
        url: string | null
        userId: string
        userName: string
        userUsername: string
        createdTime: number
        startTime: number
        endTime: number
        funded: boolean
        paymentType: 'free' | 'mana' | 'cash'
        isFree: boolean
        manaPurchaseTxnId: string | null
      }[]
    },
  },
  'get-seen-market-ids': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      contractIds: z.array(z.string()),
      types: z.array(z.enum(['page', 'card', 'promoted'])).optional(),
      since: z.number(),
    }),
    returns: [] as string[],
  },
  'search-groups': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    // Is there a way to infer return { lite:[] as LiteGroup[] } if type is 'lite'?
    returns: {
      full: [] as Group[],
      lite: [] as LiteGroup[],
    },
    props: SearchGroupParams(SearchGroupShape),
  },
  'search-my-groups': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {
      full: [] as Group[],
      lite: [] as LiteGroup[],
    },
    props: SearchGroupParams(MySearchGroupShape),
  },
  'get-groups-with-top-contracts': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: [] as { topic: Topic; contracts: Contract[] }[],
    props: z.object({}),
  },
  'get-balance-changes': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as AnyBalanceChangeType[],
    props: z
      .object({
        before: z.coerce.number().optional(),
        after: z.coerce.number().default(0),
        limit: z.coerce.number().gte(0).lte(1000).default(100),
        offset: z.coerce.number().gte(0).default(0),
        userId: z.string(),
        changeType: z.string().optional(),
      })
      .strict(),
  },
  'record-contract-view': {
    method: 'POST',
    visibility: 'public',
    authed: false,
    props: z.object({
      userId: z.string().optional(),
      contractId: z.string(),
      kind: z.enum(['page', 'card', 'promoted']),
    }),
    returns: {} as { status: 'success' },
  },
  'record-comment-view': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({
      contractId: z.string(),
      commentId: z.string(),
    }),
    returns: {} as { status: 'success' },
  },
  'record-contract-interaction': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({
      contractId: z.string(),
      kind: z.enum([
        'page bet',
        'page comment',
        'page repost',
        'page like',
        'card bet',
        'card click',
        'promoted click',
        'card like',
        'page share',
        'browse click',
      ]),
      commentId: z.string().optional(),
      feedReasons: z.array(z.string()).optional(),
      feedType: z.string().optional(),
      betGroupId: z.string().optional(),
      betId: z.string().optional(),
    }),
    returns: {} as { status: 'success' },
  },
  'get-dashboard-from-slug': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    props: z.object({
      dashboardSlug: z.string(),
    }),
    cache: DEFAULT_CACHE_STRATEGY,
    returns: {} as Dashboard,
  },
  'get-posts': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    preferAuth: true,
    cache: DEFAULT_CACHE_STRATEGY,
    props: z
      .object({
        sortBy: z
          .enum(['created_time', 'importance_score', 'new-comments'])
          .optional()
          .default('created_time'),
        term: z.string().optional(),
        limit: z.coerce.number().gte(0).lte(200).default(100),
        userId: z.string().optional(),
        offset: z.coerce.number().gte(0).default(0),
        isChangeLog: coerceBoolean.optional(),
      })
      .strict(),
    returns: [] as TopLevelPost[],
  },
  'create-public-chat-message': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as ChatMessage,
    props: z.object({
      content: contentSchema,
      channelId: z.string(),
    }),
  },
  'get-followed-groups': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z.object({
      userId: z.string(),
    }),
    returns: {} as {
      groups: Group[]
    },
  },
  'get-watched-markets': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z
      .object({
        userId: z.string(),
        term: z.string().optional(),
        limit: z.coerce
          .number()
          .gte(0)
          .lte(MAX_WATCHED_MARKETS)
          .default(WATCHED_MARKETS_PAGE_SIZE),
        offset: z.coerce.number().gte(0).default(0),
      })
      .strict(),
    returns: {} as {
      contracts: Contract[]
      totalCount: number
    },
  },
  'get-user-portfolio': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z.object({
      userId: z.string(),
    }),
    returns: {} as LivePortfolioMetrics,
  },
  'get-user-portfolio-history': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z.object({
      userId: z.string(),
      period: z.enum(PERIODS),
    }),
    returns: {} as PortfolioMetrics[],
  },
  'get-channel-memberships': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      channelId: z.coerce.number().optional(),
      createdTime: z.string().optional(),
      lastUpdatedTime: z.string().optional(),
      limit: z.coerce.number(),
    }),
    returns: {
      channels: [] as PrivateMessageChannel[],
      memberIdsByChannelId: {} as { [channelId: string]: string[] },
    },
  },
  'get-channel-messages': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      channelId: z.coerce.number(),
      limit: z.coerce.number(),
      id: z.coerce.number().optional(),
    }),
    returns: [] as PrivateChatMessage[],
  },
  'get-channel-seen-time': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      channelIds: z.array(z.coerce.number()),
    }),
    returns: [] as [number, string][],
  },
  'set-channel-seen-time': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({
      channelId: z.coerce.number(),
    }),
  },
  'get-feed': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      contracts: Contract[]
      comments: ContractComment[]
      bets: Bet[]
      reposts: Repost[]
      idsToReason: { [id: string]: string }
    },
    props: z
      .object({
        userId: z.string(),
        limit: z.coerce.number().gt(0).lte(100).default(100),
        offset: z.coerce.number().gte(0).default(0),
        ignoreContractIds: z.array(z.string()).optional(),
      })
      .strict(),
  },
  'get-mana-supply': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as ManaSupply,
    props: z.object({}).strict(),
  },
  'get-notifications': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: [] as Notification[],
    props: z
      .object({
        after: z.coerce.number().optional(),
        limit: z.coerce.number().gte(0).lte(1000).default(100),
      })
      .strict(),
  },
  'update-mod-report': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        reportId: z.number(),
        updates: z
          .object({
            status: z
              .enum(['new', 'under review', 'resolved', 'needs admin'])
              .optional(),
            mod_note: z.string().optional(),
          })
          .partial(),
      })
      .strict(),
    returns: {} as { status: string; report: ModReport },
  },
  'get-mod-reports': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        statuses: z.array(
          z.enum(['new', 'under review', 'resolved', 'needs admin'])
        ),
        limit: z.coerce.number().gte(0).lte(100).default(25),
        offset: z.coerce.number().gte(0).default(0),
        count: coerceBoolean.optional(),
        order: z.enum(['asc', 'desc']).default('desc'),
        cursorTime: z.string().datetime({ offset: true }).optional(),
        cursorId: z.coerce.number().int().optional(),
      })
      .strict(),
    returns: {} as {
      status: string
      count?: number
      reports: ModReport[]
    },
  },
  'get-txn-summary-stats': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as Row<'txn_summary_stats'>[],
    props: z
      .object({
        ignoreCategories: z.array(z.string()).optional(),
        fromType: z.string().optional(),
        toType: z.string().optional(),
        limitDays: z.coerce.number(),
      })
      .strict(),
  },
  'get-mana-summary-stats': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as Row<'mana_supply_stats'>[],
    props: z
      .object({
        limitDays: z.coerce.number(),
      })
      .strict(),
  },
  'get-active-user-mana-stats': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: [] as {
      date: string
      activeBalance: number
    }[],
    props: z
      .object({
        limitDays: z.coerce.number(),
      })
      .strict(),
  },
  // DEPRECATED: GIDX endpoints - replaced by idenfy for identity verification
  // Use create-idenfy-session and get-idenfy-status instead
  // Kept for: historical transaction processing, gradual migration
  'register-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: verificationParams,
    returns: {} as RegistrationReturnType,
  },
  'get-verification-status-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      documents?: GIDXDocument[]
      message?: string
      documentStatus?: string
    },
    props: z.object({}),
  },
  'get-monitor-status-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      message?: string
    },
    props: z.object({
      DeviceGPS: GPSProps,
    }),
  },
  'get-checkout-session-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      message?: string
      session?: CheckoutSession
    },
    props: z.object({
      PayActionCode: z.enum(['PAY', 'PAYOUT']).default('PAY'),
      DeviceGPS: GPSProps,
      userId: z.string().optional(),
      ip: z.string().optional(),
    }),
  },
  'complete-checkout-session-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      message?: string
      gidxMessage?: string | null
      details?: PaymentDetail[]
    },
    props: z.object(checkoutParams),
  },
  'complete-cashout-session-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      message?: string
      gidxMessage?: string | null
      details?: PaymentDetail[]
    },
    props: z.object(cashoutParams),
  },
  'complete-cashout-request': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      message?: string
      gidxMessage?: string | null
      details?: PaymentDetail[]
    },
    props: z.object(cashoutRequestParams),
  },
  'get-verification-documents-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      status: string
      documents: GIDXDocument[]
      utilityDocuments: GIDXDocument[]
      idDocuments: GIDXDocument[]
      rejectedDocuments: GIDXDocument[]
    },
    props: z.object({}),
  },
  'upload-document-gidx': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { status: string },
    props: z.object({
      CategoryType: z.number().gte(1).lte(7),
      fileName: z.string(),
      fileUrl: z.string(),
    }),
  },
  'get-best-comments': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { comments: ContractComment[]; contracts: Contract[] },
    props: z.object({
      limit: z.coerce.number().gte(0).lte(100).default(20),
      offset: z.coerce.number().gte(0).default(0),
      ignoreContractIds: z.array(z.string()).optional(),
      justLikes: z.coerce.number().optional(),
    }),
  },
  'get-cashouts': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    returns: [] as PendingCashoutStatusData[],
    props: z
      .object({
        limit: z.coerce.number().gte(0).lte(100).default(10),
        offset: z.coerce.number().gte(0).default(0),
        userId: z.string().optional(),
      })
      .strict(),
  },
  txns: {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z
      .object({
        token: z.string().optional(),
        offset: z.coerce.number().default(0),
        limit: z.coerce.number().gte(0).lte(100).default(100),
        before: z.coerce.number().optional(),
        after: z.coerce.number().optional(),
        toId: z.string().optional(),
        fromId: z.string().optional(),
        category: z.string().optional(),
        ignoreCategories: z.array(z.string()).optional(),
      })
      .strict(),
    returns: [] as Txn[],
  },
  'generate-ai-market-suggestions': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: [] as AIGeneratedMarket[],
    props: z
      .object({
        prompt: z.string(),
        existingTitles: z.array(z.string()).optional(),
      })
      .strict(),
  },
  'generate-ai-description': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { description: JSONContent | undefined },
    props: z
      .object({
        question: z.string(),
        description: z.string().optional(),
        answers: z.array(z.string()).optional(),
        outcomeType: z.string().optional(),
        shouldAnswersSumToOne: coerceBoolean.optional(),
        addAnswersMode: z
          .enum(['DISABLED', 'ONLY_CREATOR', 'ANYONE'])
          .optional(),
      })
      .strict(),
  },
  'generate-ai-answers': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      answers: string[]
      addAnswersMode: 'DISABLED' | 'ONLY_CREATOR' | 'ANYONE'
    },
    props: z
      .object({
        question: z.string(),
        answers: z.array(z.string()),
        shouldAnswersSumToOne: coerceBoolean,
        description: z.string().optional(),
      })
      .strict(),
  },
  'check-poll-suggestion': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      isSubjective: boolean
      confidence: number
      reason: string
    },
    props: z
      .object({
        question: z.string(),
        answers: z.array(z.string()).optional(),
      })
      .strict(),
  },
  'cast-poll-vote': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { status: string; voteId: string },
    props: z
      .object({
        contractId: z.string(),
        voteId: z.string().optional(),
        voteIds: z.array(z.string()).optional(),
        rankedVoteIds: z.array(z.string()).optional(),
      })
      .strict(),
  },
  'get-next-loan-amount': {
    method: 'GET',
    visibility: 'undocumented',
    cache: LIGHT_CACHE_STRATEGY,
    authed: false,
    returns: {} as {
      maxGeneralLoan: number
      currentLoan: number
      available: number
      dailyLimit: number
      todayLoans: number
      availableToday: number
      // Free/margin loan breakdown
      currentFreeLoan?: number
      currentMarginLoan?: number
      freeLoanAvailable?: number
      canClaimFreeLoan?: boolean
      hasMarginLoanAccess?: boolean
      // Equity-based calculation fields (equity = portfolioValue - loans)
      equity?: number
      portfolioValue?: number
      // Perp position value left out of the equity base (display only)
      perpValueExcluded?: number
    },
    props: z.object({
      userId: z.string(),
    }),
  },
  'get-market-loan-max': {
    method: 'GET',
    visibility: 'undocumented',
    cache: LIGHT_CACHE_STRATEGY,
    authed: true,
    returns: {} as {
      maxLoan: number
      currentLoan: number
      currentFreeLoan: number
      currentMarginLoan: number
      available: number
      equityLimit: number
      totalPositionValue: number
      eligible: boolean
      eligibilityReason?: string
      // Aggregate limits (tier-specific % of equity across all markets)
      aggregateLimit: number
      totalLoanAllMarkets: number
      availableAggregate: number
      // Daily limits (10% of equity per day)
      dailyLimit: number
      todayLoans: number
      availableToday: number
      // Per-answer loan data (for multi-choice markets)
      answerLoans?: Array<{
        answerId: string
        loan: number
        freeLoan: number
        marginLoan: number
        positionValue: number
      }>
    },
    props: z.object({
      contractId: z.string(),
      answerId: z.string().optional(),
    }),
  },
  'get-free-loan-available': {
    method: 'GET',
    visibility: 'undocumented',
    cache: LIGHT_CACHE_STRATEGY,
    authed: false,
    returns: {} as {
      available: number
      canClaim: boolean
      lastClaimTime: number | null
      // Breakdown by position
      positions: Array<{
        contractId: string
        answerId: string | null
        payout: number
        invested: number
        freeLoanContribution: number
      }>
      // Current totals
      currentFreeLoan: number
      currentMarginLoan: number
      totalLoan: number
      // Limits
      maxLoan: number
      dailyLimit: number
      todayLoans: number
      // Today's claimed free loan
      todaysFreeLoan: number
      // Perp position value left out of the equity base (display only)
      perpValueExcluded?: number
    },
    props: z.object({ userId: z.string() }),
  },
  'claim-free-loan': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as {
      success: boolean
      amount: number
      distributed: Array<{
        contractId: string
        answerId: string | null
        amount: number
      }>
    },
    props: z.object({}),
  },
  'create-task': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as Task,
    props: z
      .object({
        text: z.string(),
        category_id: z.number().optional(),
        priority: z.number().default(0),
        assignee_id: z.string().optional(),
      })
      .strict(),
  },
  'update-task': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        id: z.number(),
        text: z.string().optional(),
        completed: z.boolean().optional(),
        priority: z.number().optional(),
        category_id: z.number().optional(),
        archived: z.boolean().optional(),
        assignee_id: z.string().optional(),
      })
      .strict(),
  },
  'create-category': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { id: number },
    props: z
      .object({
        name: z.string(),
        color: z.string().optional(),
        displayOrder: z.number().optional(),
      })
      .strict(),
  },
  'get-categories': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: {} as { categories: TaskCategory[] },
    props: z.object({}).strict(),
  },
  'update-category': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        categoryId: z.number(),
        name: z.string().optional(),
        color: z.string().optional(),
        displayOrder: z.number().optional(),
        archived: z.boolean().optional(),
      })
      .strict(),
  },
  'get-tasks': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: {} as { tasks: Task[] },
    props: z.object({}).strict(),
  },
  'get-user-achievements': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      userId: string
      creatorTraders: number
      totalReferrals: number
      totalReferredProfitMana: number
      totalVolumeMana: number
      seasonsPlatinumOrHigher: number
      seasonsDiamondOrHigher: number
      seasonsMasters: number
      numberOfComments: number
      totalLiquidityCreatedMarkets: number
      totalTradesCount: number
      totalMarketsCreated: number
      accountAgeYears: number
      profitableMarketsCount: number
      unprofitableMarketsCount: number
      largestProfitableTradeValue: number
      largestUnprofitableTradeValue: number
      longestBettingStreak: number
      largestLeagueSeasonEarnings: number
      modTicketsResolved: number
      charityDonatedMana: number
      ranks: RanksType
    },
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
  },
  'get-user-calibration': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    // Heavy endpoint - cache for 24 hours with 1 hour stale-while-revalidate
    cache: 'public, max-age=86400, stale-while-revalidate=3600',
    returns: {} as {
      calibration: {
        yesPoints: { x: number; y: number }[]
        noPoints: { x: number; y: number }[]
        totalBets: number
      }
      performanceStats: {
        totalProfit: number
        profit365: number
        totalVolume: number
        winRate: number
        totalMarkets: number
        resolvedMarkets: number
        volatility: number
        sharpeRatio: number
        maxDrawdown: number
      }
      portfolioHistory: {
        timestamp: number
        value: number
        profit: number
      }[]
      profitByTopic: {
        topic: string
        profit: number
        volume: number
        marketCount: number
      }[]
    },
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
  },
  'get-site-activity': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      bets: Bet[]
      comments: CommentWithTotalReplies[]
      newContracts: Contract[]
      relatedContracts: Contract[]
    },
    props: z
      .object({
        limit: z.coerce.number().default(10),
        offset: z.coerce.number().default(0),
        blockedUserIds: z.array(z.string()).optional(),
        blockedGroupSlugs: z.array(z.string()).optional(),
        blockedContractIds: z.array(z.string()).optional(),
        topicIds: z.array(z.string()).optional(),
        types: z
          .array(z.enum(['bets', 'comments', 'markets', 'limit-orders']))
          .optional(),
        minBetAmount: z.coerce.number().optional(),
        onlyFollowedTopics: coerceBoolean.optional(),
        onlyFollowedContracts: coerceBoolean.optional(),
        onlyFollowedUsers: coerceBoolean.optional(),
        userId: z.string().optional(),
      })
      .strict(),
  },
  'get-browse-personalization': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    cache: 'private, no-store',
    props: z.object({}).strict(),
    returns: {} as BrowsePersonalization,
  },
  'get-unified-feed': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      // Personalized feed data
      contracts: Contract[]
      comments: ContractComment[]
      bets: Bet[]
      reposts: Repost[]
      idsToReason: { [id: string]: string }
      // Boosted markets
      boostedContracts: Contract[]
      // Activity data
      activityBets: Bet[]
      activityPerpTrades: PerpTradeActivity[]
      activityComments: CommentWithTotalReplies[]
      activityNewContracts: Contract[]
      activityRelatedContracts: Contract[]
    },
    props: z
      .object({
        userId: z.string().optional(),
        feedLimit: z.coerce.number().gt(0).lte(50).default(3),
        feedOffset: z.coerce.number().gte(0).default(0),
        activityLimit: z.coerce.number().gt(0).lte(50).default(5),
        activityOffset: z.coerce.number().gte(0).default(0),
        ignoreContractIds: z.array(z.string()).optional(),
        blockedUserIds: z.array(z.string()).optional(),
        blockedGroupSlugs: z.array(z.string()).optional(),
        blockedContractIds: z.array(z.string()).optional(),
        minBetAmount: z.coerce.number().optional(),
      })
      .strict(),
  },
  'get-sports-games': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: {} as { schedule: any[] },
    props: z.object({}).strict(),
  },
  'get-market-props': {
    method: 'GET',
    visibility: 'public',
    cache: DEFAULT_CACHE_STRATEGY,
    // Could set authed false and preferAuth with an api secret if we want it to replace static props
    authed: true,
    returns: {} as {
      manaContract: MarketContract
      chartAnnotations: ChartAnnotation[]
      topics: Topic[]
      comments: ContractComment[]
      pinnedComments: ContractComment[]
      userPositionsByOutcome: {
        YES: ContractMetric[]
        NO: ContractMetric[]
      }
      topContractMetrics: ContractMetric[]
      totalPositions: number
      dashboards: Dashboard[]
      cashContract: MarketContract
      totalManaBets: number
      totalCashBets: number
    },
    props: z.object({
      slug: z.string().optional(),
      id: z.string().optional(),
    }),
  },
  'get-user-contract-metrics-with-contracts': {
    method: 'GET',
    visibility: 'public',
    preferAuth: true,
    authed: false,
    returns: {} as {
      metricsByContract: Dictionary<ContractMetric[]>
      // NOTE: this only returns the currently used contract props to save on bandwidth
      contracts: MarketContract[]
    },
    props: z
      .object({
        userId: z.string(),
        limit: z.coerce.number().gte(0).lte(10_000).default(100),
        offset: z.coerce.number().gte(0).optional(),
        perAnswer: coerceBoolean.optional(),
        order: z.enum(['lastBetTime', 'profit']).optional(),
      })
      .strict(),
  },
  validateIap: {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    returns: {} as { success: boolean },
    props: z.object({
      receipt: z.string(),
    }),
  },
  'check-sports-event': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as { exists: boolean; existingMarket?: LiteMarket },
    props: z
      .object({
        sportsEventId: z.string(),
      })
      .strict(),
  },
  'comment-reactions': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: DEFAULT_CACHE_STRATEGY,
    returns: [] as Reaction[],
    props: z
      .object({
        contentIds: z.array(z.string()),
        contentType: z.enum(['comment', 'contract', 'social_post']),
      })
      .strict(),
  },
  'mark-all-notifications-new': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { success: boolean },
  },
  'get-contract-voters': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
    returns: [] as DisplayUser[],
  },
  'get-contract-option-voters': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    props: z.object({ contractId: z.string(), optionId: z.string() }),
    returns: [] as DisplayUser[],
  },
  'purchase-boost': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        contractId: z.string().optional(),
        postId: z.string().optional(),
        startTime: z.number().positive().finite().safe(),
        method: z.enum(['mana', 'cash', 'admin-free']),
      })
      .strict()
      .refine(
        (data) =>
          (data.contractId && !data.postId) ||
          (!data.contractId && data.postId),
        {
          message: 'Either contractId or postId must be provided, but not both',
        }
      ),
    returns: {} as { success: boolean; checkoutUrl?: string },
  },
  'remove-boost': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'generate-ai-numeric-ranges': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      thresholds: { answers: string[]; midpoints: number[] }
      buckets: { answers: string[]; midpoints: number[] }
    },
    props: z
      .object({
        question: z.string(),
        min: z.number(),
        max: z.number(),
        description: z.string().optional(),
        unit: z.string(),
      })
      .strict(),
  },
  'infer-numeric-unit': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      unit: string
    },
    props: z
      .object({
        question: z.string(),
        description: z.string().optional(),
      })
      .strict(),
  },
  'generate-ai-date-ranges': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as {
      buckets: { answers: string[]; midpoints: number[] }
      thresholds: { answers: string[]; midpoints: number[] }
    },
    props: z
      .object({
        question: z.string(),
        min: z.string(),
        max: z.string(),
        description: z.string().optional(),
      })
      .strict(),
  },
  'regenerate-numeric-midpoints': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { midpoints: number[] },
    props: z
      .object({
        description: z.string().optional(),
        question: z.string(),
        answers: z.array(z.string()),
        min: z.number(),
        max: z.number(),
        unit: z.string(),
        tab: z.enum(['thresholds', 'buckets']),
      })
      .strict(),
  },
  'regenerate-date-midpoints': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { midpoints: number[] },
    props: z
      .object({
        description: z.string().optional(),
        question: z.string(),
        answers: z.array(z.string()),
        min: z.string(),
        max: z.string(),
        tab: z.enum(['thresholds', 'buckets']),
      })
      .strict(),
  },

  'generate-concise-title': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        question: z.string(),
      })
      .strict(),
    returns: {} as { title: string },
  },
  'get-close-date': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        question: z.string(),
        utcOffset: z.number().optional(),
      })
      .strict(),
    returns: {} as { closeTime: number; confidence: number },
  },
  'refer-user': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        referredByUsername: z.string(),
        contractId: z.string().optional(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'get-referral-earnings': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as {
      total: number
      byReferredUserId: Record<
        string,
        {
          amount: number
          maxMultiplier: number
          // Which bonus types this referrer has been paid for this referred
          // user. 'signup' is the full bonus paid when the referral was
          // recorded; 'first_bet'/'verify' are the legacy split; 'legacy' means
          // a pre-split single-payment txn exists (treated as fully paid).
          bonusTypes: ('signup' | 'first_bet' | 'verify' | 'legacy')[]
        }
      >
    },
  },

  'save-market-draft': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { id: number },
    props: z
      .object({
        data: z.object({
          question: z.string(),
          description: z.any().optional(),
          // The creatable types, not a free string. A draft is a market in
          // progress, so a type create-market can never accept (PERP) has no
          // business being saved as one — storing it produced a draft that
          // looked creatable in the form and then failed at submit.
          outcomeType: z.enum(CREATEABLE_OUTCOME_TYPES),
          answers: z.array(z.string()).optional(),
          closeDate: z.string().optional(),
          closeHoursMinutes: z.string().optional(),
          neverCloses: z.boolean().optional(),
          hasManuallyEditedCloseDate: z.boolean().optional(),
          inheritedCloseTimeFrom: z.string().optional(),
          linkedToContractId: z.string().optional(),
          linkRelation: z.enum(MARKET_LINK_RELATIONS).optional(),
          visibility: z.string(),
          selectedGroups: z.array(z.any()),
          savedAt: z.number(),
        }),
      })
      .strict(),
  },

  'get-market-drafts': {
    method: 'GET',
    visibility: 'public',
    authed: true,
    returns: [] as MarketDraft[],
    props: z.object({}).strict(),
  },

  'delete-market-draft': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        id: z.coerce.number(),
      })
      .strict(),
  },
  'get-season-info': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z.object({
      season: z.coerce.number().int().positive().optional(),
    }),
    returns: {} as {
      season: number
      startTime: number // epoch ms
      endTime: number | null // epoch ms, null if a *mystery* for clients
      status: 'active' | 'processing' | 'complete'
    },
  },
  'mark-notification-read': {
    method: 'POST',
    visibility: 'private',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        notificationId: z.string(),
      })
      .strict(),
  },
  'mark-notifications-read': {
    method: 'POST',
    visibility: 'private',
    authed: true,
    returns: {} as { success: boolean },
    props: z
      .object({
        notificationIds: z.array(z.string()),
      })
      .strict(),
  },
  'dismiss-user-report': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        reportId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'create-post': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { post: TopLevelPost },
    props: z
      .object({
        title: z.string().min(1).max(160),
        content: contentSchema,
        isAnnouncement: z.boolean().optional(),
        visibility: z.enum(['public', 'unlisted']).optional(),
        isChangeLog: z.boolean().optional(),
      })
      .strict(),
  },
  'update-post': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { post: TopLevelPost },
    props: z
      .object({
        id: z.string(),
        title: z.string().min(1).max(480).optional(),
        content: contentSchema.optional(),
        visibility: z.enum(['public', 'unlisted']).optional(),
      })
      .strict(),
  },
  'create-post-comment': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { comment: PostComment },
    props: z
      .object({
        postId: z.string(),
        content: contentSchema,
        replyToCommentId: z.string().optional(),
      })
      .strict(),
  },
  'update-post-comment': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { comment: PostComment },
    props: z
      .object({
        commentId: z.string(),
        postId: z.string(),
        hidden: z.boolean(),
      })
      .strict(),
  },
  'follow-post': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    returns: {} as { success: true },
    props: z
      .object({
        postId: z.string(),
        follow: z.boolean(),
      })
      .strict(),
  },
  'edit-post-comment': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        commentId: z.string(),
        postId: z.string(),
        content: contentSchema,
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'get-user-last-active-time': {
    method: 'GET',
    authed: false,
    visibility: 'undocumented',
    cache: 'public, max-age=900, stale-while-revalidate=90', // 15 minute cache
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: {} as { lastActiveTime: number | null },
  },
  'get-monthly-bets-2025': {
    method: 'GET',
    authed: false,
    visibility: 'undocumented',
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: [] as { month: string; bet_count: number; total_amount: number }[],
  },
  'get-max-min-profit-2025': {
    method: 'GET',
    authed: false,
    visibility: 'undocumented',
    props: z
      .object({
        userId: z.string(),
      })
      .strict(),
    returns: [] as {
      profit: number
      has_yes_shares: boolean
      has_no_shares: boolean
      answer_id: string | null
      data: Contract
    }[],
  },
  'get-pending-clarifications': {
    method: 'GET',
    authed: false,
    visibility: 'undocumented',
    props: z
      .object({
        contractId: z.string(),
      })
      .strict(),
    returns: [] as {
      id: number
      contractId: string
      commentId: string
      createdTime: number
      data: { markdown: string; richText: JSONContent }
    }[],
  },
  'apply-pending-clarification': {
    method: 'POST',
    authed: true,
    visibility: 'undocumented',
    props: z
      .object({
        clarificationId: z.number(),
      })
      .strict(),
    returns: {} as { success: boolean; alreadyApplied?: boolean },
  },
  'cancel-pending-clarification': {
    method: 'POST',
    authed: true,
    visibility: 'undocumented',
    props: z
      .object({
        clarificationId: z.number(),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'get-predictle-markets': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=60, stale-while-revalidate=30',
    props: z.object({}).strict(),
    returns: {} as {
      markets: {
        id: string
        question: string
        slug: string
        creatorUsername: string
        prob: number
      }[]
      correctOrder: Record<string, number>
      dateString: string
      puzzleNumber: number
    },
  },
  'save-predictle-result': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        puzzleNumber: z.number(),
        won: z.boolean(),
        // Full game state for cross-device sync
        gameState: z.object({
          orderedMarketIds: z.array(z.string()),
          attempts: z.array(
            z.object({
              marketId: z.string(),
              feedback: z.array(z.enum(['correct', 'incorrect'])),
            })
          ),
        }),
      })
      .strict(),
    returns: {} as { success: boolean },
  },
  'get-predictle-result': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ puzzleNumber: z.coerce.number() }).strict(),
    returns: {} as {
      hasResult: boolean
      result?: {
        won: boolean
        gameState: {
          orderedMarketIds: string[]
          attempts: {
            marketId: string
            feedback: ('correct' | 'incorrect')[]
          }[]
        }
      }
    },
  },
  'get-charity-giveaway': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        giveawayNum: z.coerce.number().optional(),
        userId: z.string().optional(),
      })
      .strict(),
    returns: {} as {
      giveaway?: {
        giveawayNum: number
        name: string
        prizeAmountUsd: number
        closeTime: number
        winningTicketId: string | null
        createdTime: number
      }
      charityStats: {
        charityId: string
        totalTickets: number
        totalManaSpent: number
      }[]
      totalTickets: number
      winningCharity?: string
      winner?: {
        id: string
        username: string
        name: string
        avatarUrl: string
      }
      champion?: {
        id: string
        username: string
        name: string
        avatarUrl: string
        totalTickets: number
      }
      topUsers?: {
        id: string
        username: string
        name: string
        avatarUrl: string
        totalTickets: number
        rank: number
      }[]
      yourEntry?: {
        rank: number
        totalTickets: number
      }
      trophyHolder?: {
        id: string
        username: string
        name: string
        avatarUrl: string
        totalTickets: number
        claimedTime: number
      }
      previousTrophyHolder?: {
        id: string
        username: string
        name: string
        avatarUrl: string
      }
      // Provably fair: nonce contains the Bitcoin block hash used for winner selection
      // Only revealed AFTER winner is selected. Users verify by finding first block after closeTime.
      nonce?: string
    },
  },
  'get-charity-giveaway-list': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z.object({}).strict(),
    returns: {} as {
      giveaways: Array<{
        giveawayNum: number
        name: string
        closeTime: number
        createdTime: number
        hasWinner: boolean
      }>
    },
  },
  'get-crypto-purchase-status': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as {
      hasCryptoPurchase: boolean
    },
  },
  'create-daimo-session': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        offerId: z.string().optional(),
      })
      .strict(),
    returns: {} as {
      sessionId: string
      clientSecret: string
    },
  },
  'get-personalized-mana-offers': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as PersonalizedManaOfferSummary,
  },
  'activate-personalized-mana-offers': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as PersonalizedManaOfferSummary,
  },
  'release-personalized-mana-offer-lock': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ offerId: z.string() }).strict(),
    returns: {} as { success: boolean },
  },
  'dismiss-personalized-mana-offer': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    // Global dismiss: applies to all active offers for the calling user.
    // dismissed=false un-dismisses them (used by the "show hidden offer(s)"
    // chip on /checkout).
    props: z.object({ dismissed: z.boolean() }).strict(),
    returns: {} as PersonalizedManaOfferSummary,
  },
  'admin-create-charity-giveaway': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        closeTime: z.number(),
        prizeAmountUsd: z.number(),
      })
      .strict(),
    returns: {} as {
      giveawayNum: number
    },
  },
  'admin-update-charity-giveaway-prize': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        giveawayNum: z.number(),
        prizeAmountUsd: z.number().finite().positive(),
      })
      .strict(),
    returns: {} as {
      giveawayNum: number
      prizeAmountUsd: number
    },
  },
  'buy-charity-giveaway-tickets': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        giveawayNum: z.number(),
        charityId: z.string(),
        numTickets: z.number().positive(),
      })
      .strict(),
    returns: {} as {
      ticketId: string
      numTickets: number
      manaSpent: number
    },
  },
  'get-charity-giveaway-sales': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        giveawayNum: z.coerce.number(),
        limit: z.coerce.number().min(1).max(100).default(50),
        before: z.string().optional(),
      })
      .strict(),
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      sales: {
        id: string
        giveawayNum: number
        charityId: string
        userId: string
        numTickets: number
        manaSpent: number
        createdTime: number
      }[]
    },
  },
  'select-charity-giveaway-winner': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ giveawayNum: z.number() }).strict(),
    returns: {} as {
      ticketId: string
      charityId: string
      userId: string
      blockHash: string
      blockHeight: number
    },
  },
  'get-sweepstakes': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z.object({ sweepstakesNum: z.coerce.number().optional() }).strict(),
    returns: {} as {
      sweepstakes?: {
        sweepstakesNum: number
        name: string
        prizes: {
          rank?: number
          rankStart?: number
          rankEnd?: number
          amountUsdc: number
          label: string
        }[]
        closeTime: number
        winningTicketIds: string[] | null
        createdTime: number
        announcementSent: boolean
      }
      userStats: {
        userId: string
        totalTickets: number
        totalManaSpent: number
      }[]
      totalTickets: number
      totalManaSpent?: number
      participantCount?: number
      winners?: {
        rank: number
        label: string
        prizeUsdc: number
        ticketId: string
        user: {
          id: string
          username: string
          name: string
          avatarUrl: string
        }
      }[]
      // Provably fair: nonce contains the Bitcoin block hash used for winner selection
      // Only revealed AFTER winners are selected. Users verify by finding first block after closeTime.
      nonce?: string
      // Free ticket status for current user
      hasClaimedFreeTicket?: boolean
      // Investment requirement fields (for current user)
      userTotalManaInvested?: number
      meetsInvestmentRequirement?: boolean
      minManaInvested?: number
      // Set when an admin voided this user's entries in this drawing (and
      // refunded their mana). Lets the page explain the dropped entry count.
      userVoidedEntries?: number
      userVoidedManaRefunded?: number
    },
  },
  'get-sweepstakes-list': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    // Wait for Firebase auth to settle before firing — the per-drawing
    // userStatus icons depend on auth.uid, and without preferAuth the
    // first call races auth-loading and comes back with everything null,
    // which then gets cached for the rest of the session.
    preferAuth: true,
    props: z.object({}).strict(),
    returns: {} as {
      sweepstakes: Array<{
        sweepstakesNum: number
        name: string
        closeTime: number
        createdTime: number
        hasWinners: boolean
        totalPrizeUsd: number
        // Per-user claim status for surfacing icons. Null when no relevant
        // state (unauthenticated, didn't win, or rejected/opted_out).
        userStatus: 'paid' | 'pending' | 'action-needed' | null
      }>
    },
  },
  'admin-create-sweepstakes': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        closeTime: z.number(),
        prizes: z
          .array(
            z.object({
              rank: z.number(),
              amountUsdc: z.number(),
              label: z.string(),
            })
          )
          .min(1),
      })
      .strict(),
    returns: {} as {
      sweepstakesNum: number
    },
  },
  'check-sweepstakes-geo': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    // Don't cache — the user's IP can change between visits (VPN toggle,
    // mobile network handoff) and an incorrect cached "allowed" would let
    // a restricted user see the buy UI.
    props: z.object({}).strict(),
    returns: {} as { allowed: boolean },
  },
  'admin-announce-prize-drawing': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        sweepstakesNum: z.number(),
        // When true, returns the title + body that *would* be sent
        // without actually firing notifications or flipping the
        // announcement_sent flag. Used by the admin UI for the preview.
        dryRun: z.boolean().optional(),
      })
      .strict(),
    returns: {} as {
      sweepstakesNum: number
      title: string
      body: string
      totalPrizeUsd: number
      winnerCount: number
      closeTime: number
      alreadySent: boolean
      sent: boolean
    },
  },
  'buy-sweepstakes-tickets': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        sweepstakesNum: z.number(),
        numTickets: z.number().positive().optional(),
        maxManaSpent: z.number().positive().optional(),
      })
      .strict()
      .refine((props) => props.numTickets || props.maxManaSpent, {
        message: 'Must provide numTickets or maxManaSpent',
      }),
    returns: {} as {
      ticketId: string
      numTickets: number
      manaSpent: number
    },
  },
  'claim-free-sweepstakes-ticket': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ sweepstakesNum: z.number() }).strict(),
    returns: {} as {
      ticketId: string
      numTickets: number
    },
  },
  'get-sweepstakes-sales': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        sweepstakesNum: z.coerce.number(),
        limit: z.coerce.number().min(1).max(100).default(50),
        before: z.string().optional(),
      })
      .strict(),
    cache: LIGHT_CACHE_STRATEGY,
    returns: {} as {
      sales: {
        id: string
        sweepstakesNum: number
        userId: string
        numTickets: number
        manaSpent: number
        isFree: boolean
        createdTime: number
      }[]
    },
  },
  'select-sweepstakes-winners': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ sweepstakesNum: z.number() }).strict(),
    returns: {} as {
      winners: {
        rank: number
        label: string
        prizeUsdc: number
        ticketId: string
        userId: string
      }[]
      blockHash: string
      blockHeight: number
    },
  },
  'check-bitcoin-block': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z.object({ closeTime: z.coerce.number() }).strict(),
    returns: {} as
      | { available: false }
      | {
          available: true
          blockHeight: number
          blockHash: string
          blockTimestamp: number
        },
  },
  'claim-sweepstakes-prize': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        sweepstakesNum: z.number(),
        walletAddress: z
          .string()
          .regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid Ethereum address'),
      })
      .strict(),
    returns: {} as {
      success: boolean
      claimId: string
    },
  },
  'get-sweepstakes-prize-claim': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ sweepstakesNum: z.coerce.number() }).strict(),
    returns: {} as {
      claim: {
        id: string
        rank: number
        prizeAmountUsdc: number
        // Null when admin recorded an opted_out / rejected status before
        // the user ever submitted a wallet.
        walletAddress: string | null
        paymentStatus: 'awaiting' | 'sent' | 'rejected' | 'opted_out'
        paymentTxnHash: string | null
        createdTime: number
      } | null
      winnerInfo: {
        rank: number
        prizeAmountUsdc: number
      } | null
    },
  },
  'admin-get-prize-claims': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ sweepstakesNum: z.coerce.number().optional() }).strict(),
    returns: {} as {
      claims: Array<{
        id: string | null
        sweepstakesNum: number
        userId: string
        username: string
        name: string
        avatarUrl: string
        rank: number
        prizeAmountUsdc: number
        walletAddress: string | null
        paymentStatus: 'awaiting' | 'sent' | 'rejected' | 'opted_out' | null
        paymentTxnHash: string | null
        createdTime: number | null
      }>
    },
  },
  'admin-update-prize-payment': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    // Identify the claim by either an existing claimId (user already
    // submitted a wallet) or by (sweepstakesNum, userId) — the latter lets
    // admins record opted_out/rejected decisions for winners who never
    // submitted a wallet, by upserting a row with NULL wallet_address.
    props: z
      .object({
        claimId: z.string().optional(),
        sweepstakesNum: z.number().int().optional(),
        userId: z.string().optional(),
        paymentStatus: z.enum(['awaiting', 'sent', 'rejected', 'opted_out']),
        paymentTxnHash: z.string().optional(),
      })
      .strict()
      .refine(
        (v) => !!v.claimId || (v.sweepstakesNum !== undefined && !!v.userId),
        { message: 'Provide either claimId or (sweepstakesNum, userId)' }
      ),
    returns: {} as { success: boolean },
  },
  'admin-delete-prize-claim': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ claimId: z.string() }).strict(),
    returns: {} as { success: boolean },
  },
  'admin-get-mana-sales': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ limit: z.coerce.number().optional() }).strict(),
    returns: {} as {
      sales: Array<{
        id: string
        createdTime: number
        userId: string
        username: string
        name: string
        avatarUrl: string
        amount: number
        paidInCents: number | null
        paymentType: 'stripe' | 'apple' | 'gidx' | 'crypto' | 'unknown'
      }>
    },
  },
  'admin-get-top-whale-users': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ limit: z.coerce.number().optional() }).strict(),
    returns: {} as {
      users: Array<{
        userId: string
        username: string
        name: string
        avatarUrl: string
        totalPaidCents: number
        totalMana: number
        purchaseCount: number
        lastPurchaseTime: number
        byType: {
          stripe: { paidCents: number; mana: number; count: number }
          apple: { paidCents: number; mana: number; count: number }
          gidx: { paidCents: number; mana: number; count: number }
          crypto: { paidCents: number; mana: number; count: number }
          unknown: { paidCents: number; mana: number; count: number }
        }
      }>
    },
  },
  'admin-get-new-users': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ limit: z.coerce.number().optional() }).strict(),
    returns: {} as {
      users: Array<{
        id: string
        createdTime: number
        username: string
        name: string
        avatarUrl: string
        balance: number
        isBannedFromPosting: boolean
        referredByUserId: string | null
        referredByUsername: string | null
        referredByName: string | null
        bonusEligibility:
          | 'verified'
          | 'grandfathered'
          | 'eligible'
          | 'ineligible'
          | 'requires_verification'
          | null
        purchasedMana: boolean
        email: string | null
        ipAddress: string | null
      }>
    },
  },
  'get-predictle-percentile': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        puzzleNumber: z.coerce.number(),
        attempts: z.coerce.number(),
      })
      .strict(),
    returns: {} as {
      percentile: number // 0-100, percentage of users you beat
      totalUsers: number
    },
  },
  // Shop endpoints
  'get-shop-items': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    props: z.object({}).strict(),
    returns: [] as ShopItem[],
  },
  'shop-purchase': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        itemId: z.string(),
      })
      .strict(),
    returns: {} as {
      success: boolean
      entitlement?: UserEntitlement
      entitlements: UserEntitlement[]
      upgradeCredit?: number
    },
  },
  'shop-toggle': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        itemId: z.string(),
        enabled: z.boolean(),
      })
      .strict(),
    returns: {} as { success: boolean; entitlements: UserEntitlement[] },
  },
  'shop-cancel-subscription': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { success: boolean; entitlements: UserEntitlement[] },
  },
  'shop-update-metadata': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        itemId: z.string(),
        metadata: z
          .record(z.union([z.string().max(500), z.number()]))
          .refine((obj) => Object.keys(obj).length <= 10, {
            message: 'metadata cannot have more than 10 keys',
          }),
      })
      .strict(),
    returns: {} as { success: boolean; entitlements: UserEntitlement[] },
  },
  'shop-reset-all': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { success: boolean; refundedAmount: number },
  },
  'get-ticket-stock': {
    method: 'GET',
    visibility: 'public',
    authed: false,
    cache: 'public, max-age=2, stale-while-revalidate=10',
    props: z.object({ itemId: z.string().optional() }).strict(),
    returns: {} as { sold: number; maxStock: number; available: number },
  },
  'get-ticket-orders': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ itemId: z.string().optional() }).strict(),
    returns: {} as {
      orders: Array<{
        id: string
        userId: string
        username: string
        displayName: string
        email: string | null
        itemId: string
        priceMana: number
        status: string
        createdTime: number
      }>
      total: number
    },
  },
  'shop-purchase-merch': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        itemId: z.string(),
        variantId: z.string(),
        shippingCost: z.number().int().min(0),
        shipping: z.object({
          name: z.string().max(200),
          address1: z.string().max(200),
          address2: z.string().max(200).optional(),
          city: z.string().max(100),
          state: z.string().max(100).optional(),
          zip: z.string().max(20).optional(),
          country: z.string().regex(/^[A-Z]{2}$/),
          taxNumber: z.string().max(50).optional(),
          email: z.string().email().max(254).optional(),
        }),
      })
      .strict(),
    returns: {} as {
      success: boolean
      printfulOrderId: string
      printfulStatus: string
    },
  },
  'shop-shipping-rates': {
    method: 'POST',
    visibility: 'public',
    authed: true,
    props: z
      .object({
        variantId: z.string(),
        address: z.object({
          address1: z.string().max(200),
          city: z.string().max(100),
          state: z.string().max(100).optional(),
          zip: z.string().max(20).optional(),
          country: z.string().regex(/^[A-Z]{2}$/),
          taxNumber: z.string().max(50).optional(),
        }),
      })
      .strict(),
    returns: {} as {
      rates: Array<{
        id: string
        name: string
        rate: string
        currency: string
        minDeliveryDays: number
        maxDeliveryDays: number
      }>
    },
  },
  'claim-charity-champion': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ enabled: z.boolean().optional() }).strict(),
    returns: {} as { success: boolean; entitlements: UserEntitlement[] },
  },
  // Admin spam detection endpoints
  'get-suspected-spam-comments': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        limit: z.coerce.number().min(1).max(1000).default(20),
        offset: z.coerce.number().default(0),
        ignoredIds: z.array(z.string()).optional(),
      })
      .strict(),
    returns: {} as {
      comments: {
        commentId: string
        contractId: string
        content: JSONContent | null
        commentText: string
        marketTitle: string
        marketSlug: string
        creatorUsername: string
        userId: string
        userName: string
        userUsername: string
        userAvatarUrl: string | null
        createdTime: number
        isSpam: boolean | null
      }[]
      total: number
    },
  },
  'delete-spam-comments': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        commentIds: z.array(z.string()),
      })
      .strict(),
    returns: {} as { success: boolean; deletedCount: number },
  },
  'get-bettors-from-bet-ids': {
    method: 'GET',
    visibility: 'undocumented',
    cache: 'public, max-age=600, stale-while-revalidate=30',
    authed: false,
    props: z
      .object({
        betIds: z.array(z.string()).max(50),
      })
      .strict(),
    returns: {} as Record<
      string,
      { id: string; username: string; name: string }
    >,
  },
  'get-top-markets-yesterday': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=3600, stale-while-revalidate=600',
    props: z.object({}).strict(),
    returns: {} as {
      topByTraders: {
        contract: Contract
        tradersYesterday: number
      }[]
      topByViews: {
        contract: Contract
        viewsYesterday: number
      }[]
    },
  },
  'get-shop-stats': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        limitDays: z.coerce.number().int().min(1).max(365),
      })
      .strict(),
    returns: {} as {
      subscriptionSales: {
        date: string
        itemId: string
        quantity: number
        revenue: number
      }[]
      digitalGoodsSales: {
        date: string
        itemId: string
        quantity: number
        revenue: number
      }[]
      merchSales: {
        date: string
        itemId: string
        quantity: number
        revenue: number
      }[]
      subscribersByTier: {
        tier: 'basic' | 'plus' | 'premium'
        count: number
        autoRenewCount: number
      }[]
      subscriptionsOverTime: {
        date: string
        basicCount: number
        plusCount: number
        premiumCount: number
        totalCount: number
      }[]
    },
  },
  // iDenfy identity verification endpoints
  'create-idenfy-session': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { redirectUrl: string; scanRef: string },
  },
  'get-idenfy-status': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as {
      status: 'pending' | 'approved' | 'denied' | 'suspected' | null
      verifiedTime: number | null
    },
  },
  'get-idenfy-stats': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: LIGHT_CACHE_STRATEGY,
    props: z
      .object({
        limitDays: z.coerce.number(),
      })
      .strict(),
    returns: {} as {
      dailyStats: {
        date: string
        approvals: number
        denials: number
        pending: number
        suspected: number
      }[]
    },
  },
  // Admins only: the /stats page funnels. Internal product metrics, so they
  // are not in daily_stats or the page's static props.
  'get-surface-funnels': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        limitDays: z.coerce.number().int().min(1).max(400).default(120),
      })
      .strict(),
    returns: {} as {
      // funnels is a SurfaceFunnels value (common/src/stat-surfaces.ts);
      // read it with parseSurfaceFunnels.
      days: { date: string; funnels: unknown }[]
    },
  },
  // Merch admin endpoints
  'get-merch-orders': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        limit: z.coerce.number().int().min(1).max(2000).default(25),
        offset: z.coerce.number().int().min(0).default(0),
        dateRange: z
          .enum(['all', 'week', 'month', '3-months', '6-months', 'year'])
          .default('all'),
      })
      .strict(),
    returns: {} as {
      orders: (ShopOrder & { username: string; displayName: string })[]
      total: number
    },
  },
  'get-merch-stock-status': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z.object({}).strict(),
    returns: {} as { outOfStockItems: string[] },
  },
  'toggle-merch-stock': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        itemId: z.string(),
      })
      .strict(),
    returns: {} as { itemId: string; outOfStock: boolean },
  },
  'cancel-merch-order': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        orderId: z.string(),
      })
      .strict(),
    returns: {} as { success: boolean; refundedAmount: number },
  },
  'get-user-merch-orders': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { orders: ShopOrder[] },
  },

  // ─── Sports Admin ────────────────────────────────────────────────────────────

  'admin-sports-fixtures': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionCode: z.string(),
        dateFrom: z.string(),
        dateTo: z.string(),
        stage: z.string().optional(),
      })
      .strict(),
    returns: {} as {
      fixtures: Array<{
        id: number
        homeTeam: { name: string; tla: string; crest: string }
        awayTeam: { name: string; tla: string; crest: string }
        homeFlag: string
        awayFlag: string
        utcDate: string
        stageCode: string
        stageLabel: string
        group: string | null
        status: string
        closeTime: number
        liquidityTier: number
        existingMarketId: string | null
        sportsEventId: string
      }>
    },
  },

  'admin-sports-create-markets': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionCode: z.string(),
        matchIds: z.array(z.number()),
        dryRun: z.boolean(),
        customNote: z.string().optional(),
        dashboardUrl: z.string().optional(),
        liquidityTierOverrides: z.record(z.string(), z.number()).optional(),
        // Extra topic/group slugs to tag every created market with, on top of
        // the tournament's configured groups. Unknown slugs are ignored.
        extraGroupSlugs: z.array(z.string()).optional(),
      })
      .strict(),
    returns: {} as {
      groupId: string
      groupCreated: boolean
      groupRestricted: boolean
      communityGroupId: string
      communityGroupCreated: boolean
      communityDashboardId: string
      communityDashboardCreated: boolean
      results: Array<{
        matchId: number
        status: 'created' | 'skipped' | 'dry-run' | 'error'
        question: string
        marketId: string | null
        reason: string | null
      }>
    },
  },

  'admin-sports-community-market': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionCode: z.string(),
        contractId: z.string(),
        action: z.enum(['add', 'remove']),
      })
      .strict(),
    returns: {} as {
      success: boolean
      dashboardId: string
      action: 'add' | 'remove'
      contractId: string
    },
  },

  'admin-sports-init-community': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ competitionCode: z.string() }).strict(),
    returns: {} as {
      groupId: string
      groupCreated: boolean
      dashboardId: string
      dashboardCreated: boolean
    },
  },

  'sports-markets': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        sportsLeague: z.string(),
      })
      .strict(),
    returns: {} as {
      markets: SportsMarket[]
    },
  },

  'create-social-post': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        content: socialPostDraftSchema,
        parentId: z.string().optional(),
        source: socialPostSourceSchema.optional(),
      })
      .strict()
      .refine(
        ({ content, source }) =>
          hasSocialPostContent(content) || !!(source && 'postId' in source),
        'Add text, a market, an image, or a quoted post'
      ),
    returns: {} as SocialPost,
  },
  'edit-social-post': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ id: z.string(), content: socialPostEditSchema }).strict(),
    returns: {} as SocialPost,
  },
  'delete-social-post': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z.object({ id: z.string() }).strict(),
    returns: {} as { success: true },
  },
  'get-social-posts': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    cache: 'no-store',
    props: z
      .object({
        parentId: z.string().optional(),
        ids: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
        forModeration: z
          .enum(['true', 'false'])
          .transform((value) => value === 'true')
          .optional(),
        useCache: z
          .enum(['true', 'false'])
          .transform((value) => value === 'true')
          .optional(),
        cursor: socialCursorSchema.optional(),
        limit: z.coerce.number().int().min(1).max(30).default(20),
      })
      .strict()
      .refine(
        ({ ids, parentId, cursor, useCache }) =>
          !ids || (!parentId && !cursor && !useCache),
        'ID lookups cannot be combined with timeline pagination or caching'
      )
      .refine(
        ({ forModeration, ids }) => !forModeration || !!ids,
        'Moderation lookups require post IDs'
      ),
    returns: {} as SocialPostPage,
  },
  'get-social-post': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    cache: 'no-store',
    props: z.object({ id: z.string() }).strict(),
    returns: {} as SocialPostDetail,
  },
  'get-social-likers': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    cache: 'no-store',
    props: z
      .object({
        id: z.string(),
        cursor: socialCursorSchema.optional(),
        limit: z.coerce.number().int().min(1).max(50).default(30),
      })
      .strict(),
    returns: {} as SocialLikerPage,
  },
  // Upcoming, live and just-finished games across every sport, each with the
  // ids of its related markets (props, totals, community side-bets). Powers the
  // /sports schedule. Probabilities move over websockets after the first load,
  // so a short shared cache keeps the fan-out cheap.
  'sports-schedule': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=30, stale-while-revalidate=60',
    props: z
      .object({
        // 'all', a curated sport key, or a sport from a Sports subtopic.
        sport: z.string().regex(SPORT_KEY_RE).optional(),
        daysAhead: z.coerce.number().int().min(1).max(60).optional(),
        limit: z.coerce.number().int().min(1).max(400).optional(),
        // nextCursor from the previous page.
        cursor: z
          .string()
          .regex(/^\d+_[A-Za-z0-9]+$/)
          .max(64)
          .optional(),
        includeRelated: coerceBoolean.optional(),
        // A topic under Sports (SportsSubtopic.id): only its games and markets.
        topic: z.string().min(1).max(MAX_ID_LENGTH).optional(),
      })
      .strict(),
    returns: {} as SportsScheduleResponse,
  },
  // Every sport on the sports pages and every other topic under Sports, with
  // the topics behind each. Topic pages use it to send a topic to its page
  // there.
  'sports-index': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=300, stale-while-revalidate=600',
    props: z.object({}).strict(),
    // `topics` is absent from an API that predates it.
    returns: {} as { sports: SportInfo[]; topics?: SportsSubtopic[] },
  },
  // A sport's leaderboards ('all' for every sport), or with `topic` a topic's
  // under Sports, and with `userId` where that user stands on them. The
  // scheduler rebuilds them hourly. Empty boards for a sport or topic it
  // doesn't know.
  'sports-leaderboards': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    cache: 'public, max-age=300, stale-while-revalidate=600',
    props: z
      .object({
        sport: z.string().regex(SPORT_KEY_RE).optional(),
        topic: z.string().min(1).max(MAX_ID_LENGTH).optional(),
        userId: z.string().min(1).max(MAX_ID_LENGTH).optional(),
      })
      .strict(),
    returns: {} as SportsLeaderboards,
  },

  // Link a market to the market it's about (common/market-links), replacing
  // any parent it had. The market's creator or a mod.
  'link-market': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        contractId: z.string().min(1).max(MAX_ID_LENGTH),
        parentContractId: z.string().min(1).max(MAX_ID_LENGTH),
        relation: z.enum(MARKET_LINK_RELATIONS).optional(),
      })
      .strict(),
    returns: {} as { success: true },
  },
  // Remove a market's link: its creator, the parent's creator, or a mod.
  'unlink-market': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        contractId: z.string().min(1).max(MAX_ID_LENGTH),
      })
      .strict(),
    returns: {} as { success: true },
  },
  // What a market is linked to and what's linked to it: a market's info
  // dialog, and the markets under a race on the election map.
  'get-market-links': {
    method: 'GET',
    visibility: 'undocumented',
    authed: false,
    props: z
      .object({
        contractId: z.string().min(1).max(MAX_ID_LENGTH),
      })
      .strict(),
    returns: {} as MarketLinks,
  },

  // The scheduler switches on /admin/sports: one per Odds API calendar phase.
  'admin-sports-competition-switches': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as { switches: CompetitionSwitchState[] },
  },
  'admin-sports-set-competition-switch': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionId: z.string().max(100),
        phase: z.string().max(100),
        on: z.boolean(),
      })
      .strict(),
    returns: {} as { on: boolean },
  },
  // How often the sports resolver asks for scores, and the credits it spends.
  'admin-sports-score-polling': {
    method: 'GET',
    visibility: 'undocumented',
    authed: true,
    props: z.object({}).strict(),
    returns: {} as ScorePollingPanel,
  },
  'admin-sports-set-score-polling': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        // 'sport:<sport id>', 'game:<contract id>' or 'finals'.
        target: z.string().max(60),
        // Seconds; 0 is off. Null goes back to the default.
        intervalSeconds: z.number().int().min(0).max(3600).nullable(),
      })
      .strict(),
    returns: {} as { intervalSeconds: number | null },
  },
  'admin-sports-create-odds-markets': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionId: z.string(),
        dryRun: z.boolean().optional(),
        // Provider event ids from a dry run. Only these games are created.
        eventIds: z.array(z.string()).max(200).optional(),
      })
      .strict(),
    returns: {} as {
      created: number
      skipped: number
      errors: number
      results: Array<{
        eventId: string
        question: string
        status: 'created' | 'skipped' | 'dry-run' | 'error'
        reason: string | null
      }>
    },
  },
  // F1 markets from BALLDONTLIE for the next F1_DAYS_AHEAD days' sessions, by
  // hand, on dev or prod. The daily sports-f1-create job makes them too.
  'admin-sports-create-f1-markets': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionId: z.string(),
        dryRun: z.boolean().optional(),
        // `<session id>:<market type>` keys from a dry run. Only these are created.
        keys: z.array(z.string().max(40)).max(100).optional(),
      })
      .strict(),
    returns: {} as {
      created: number
      skipped: number
      errors: number
      results: Array<{
        key: string
        question: string
        status: 'created' | 'skipped' | 'dry-run' | 'error'
        reason: string | null
      }>
    },
  },

  'admin-sports-resolve': {
    method: 'POST',
    visibility: 'undocumented',
    authed: true,
    props: z
      .object({
        competitionCode: z.string(),
      })
      .strict(),
    returns: {} as {
      resolved: number
      skipped: number
      errors: number
      log: Array<{
        question: string
        result: string
        status: 'resolved' | 'skipped' | 'error'
      }>
    },
  },
} as const)

export type APIPath = keyof typeof API
export type APISchema<N extends APIPath> = (typeof API)[N]

export type APIParams<N extends APIPath> = z.input<APISchema<N>['props']>
export type ValidatedAPIParams<N extends APIPath> = z.output<
  APISchema<N>['props']
>

export type APIResponse<N extends APIPath> = APISchema<N> extends {
  returns: Record<string, any>
}
  ? APISchema<N>['returns']
  : void

export type APIResponseOptionalContinue<N extends APIPath> =
  | { continue: () => Promise<void>; result: APIResponse<N> }
  | APIResponse<N>
